// client/src/coms/views/canvas_parts/canvas_room_view_parts/canvasGpuCore.js
import { getImportedBrushGpuTipCanvas } from './importedBrushRenderer';
import { getPencilBrushTipCanvas } from './pencilUtils';
import { buildDryMediaVertices, createDryMediaRenderer, isDryMedium, mediumDensity } from './dryMedia';
import { createOilPaintGpuSystem } from './oilPaintGpu';
import { createPhysicalPaintSystem } from '../../../canvas/paint-engine/integration/PhysicalPaintSystem';
import { getReadyPaintDevice, preparePhysicalPaintDevice } from '../../../canvas/paint-engine/integration/physicalPaintFlag';
import {
  SURFACE_CONTACT_GLSL,
  calibrateProfile,
  contactProfileFor,
  drawingToothStrength,
  getSurfaceMedia,
  normaliseToothField,
  paperCalibration,
  paperToothTile,
  publishToothField,
} from '../../../canvas/paint-engine/integration/surfaceContact';

import {
  buildJitterBlobProfile,
  combineSeed,
  createJitterBrushSettings,
  getJitterBlobScale,
  getJitterBrushSpacing,
} from './brushJitter';

// Physical paint (WebGPU) rollout: start acquiring the device early when the flag is on.
preparePhysicalPaintDevice();


export function ensureGpuLayersForList() {
  return null;
}

export function gpuCompositeLayers() {
  return false;
}

function compileShader(gl, type, source) {
  const shader = gl.createShader(type);
  if (!shader) throw new Error('Unable to create WebGL shader.');
  gl.shaderSource(shader, source);
  gl.compileShader(shader);
  if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
    const message = gl.getShaderInfoLog(shader) || 'WebGL shader compilation failed.';
    gl.deleteShader(shader);
    throw new Error(message);
  }
  return shader;
}

export function createProgram(gl, vertexSource, fragmentSource) {
  const vertexShader = compileShader(gl, gl.VERTEX_SHADER, vertexSource);
  const fragmentShader = compileShader(gl, gl.FRAGMENT_SHADER, fragmentSource);
  const program = gl.createProgram();
  if (!program) throw new Error('Unable to create WebGL program.');
  gl.attachShader(program, vertexShader);
  gl.attachShader(program, fragmentShader);
  gl.linkProgram(program);
  gl.deleteShader(vertexShader);
  gl.deleteShader(fragmentShader);
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    const message = gl.getProgramInfoLog(program) || 'WebGL program link failed.';
    gl.deleteProgram(program);
    throw new Error(message);
  }
  return program;
}

function createTexture(gl, width, height) {
  const texture = gl.createTexture();
  if (!texture) throw new Error('Unable to create GPU texture.');
  gl.bindTexture(gl.TEXTURE_2D, texture);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, width, height, 0, gl.RGBA, gl.UNSIGNED_BYTE, null);
  gl.bindTexture(gl.TEXTURE_2D, null);
  return texture;
}

function createFramebuffer(gl, texture) {
  const framebuffer = gl.createFramebuffer();
  if (!framebuffer) throw new Error('Unable to create GPU framebuffer.');
  gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
  gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, gl.TEXTURE_2D, texture, 0);
  if (gl.checkFramebufferStatus(gl.FRAMEBUFFER) !== gl.FRAMEBUFFER_COMPLETE) {
    gl.deleteFramebuffer(framebuffer);
    throw new Error('GPU framebuffer is incomplete.');
  }
  gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  return framebuffer;
}

function hexToColor(hex) {
  const raw = String(hex || '#111111').replace('#', '').trim();
  const value = raw.length === 3
    ? raw.split('').map((character) => `${character}${character}`).join('')
    : raw.padEnd(6, '0').slice(0, 6);
  return [
    (Number.parseInt(value.slice(0, 2), 16) || 0) / 255,
    (Number.parseInt(value.slice(2, 4), 16) || 0) / 255,
    (Number.parseInt(value.slice(4, 6), 16) || 0) / 255,
  ];
}

function clamp(value, minimum, maximum) {
  return Math.max(minimum, Math.min(maximum, Number(value) || 0));
}

function hash(value) {
  const x = Math.sin(value * 12.9898 + 78.233) * 43758.5453;
  return x - Math.floor(x);
}

function getPenTiltTransform(point, radius) {
  const safeRadius = Math.max(0.5, Number(radius) || 0.5);
  const altitudeAngle = Number(point && point.altitudeAngle);
  const azimuthAngle = Number(point && point.azimuthAngle);
  const tiltX = Number(point && (point.tiltX ?? point.pointerTiltX));
  const tiltY = Number(point && (point.tiltY ?? point.pointerTiltY));

  let tiltFactor = 0;
  if (Number.isFinite(altitudeAngle)) {
    tiltFactor = clamp(1 - (altitudeAngle / (Math.PI * 0.5)), 0, 1);
  } else if (Number.isFinite(tiltX) && Number.isFinite(tiltY)) {
    tiltFactor = clamp(Math.hypot(tiltX, tiltY) / 90, 0, 1);
  }

  let rotation = 0;
  if (Number.isFinite(azimuthAngle)) {
    rotation = azimuthAngle + (Math.PI * 0.5);
  } else if (Number.isFinite(tiltX) && Number.isFinite(tiltY) && Math.hypot(tiltX, tiltY) > 0.5) {
    rotation = Math.atan2(tiltY, tiltX) + (Math.PI * 0.5);
  }

  return {
    radiusX: safeRadius * (1 + (tiltFactor * 0.85)),
    radiusY: safeRadius * (1 - (tiltFactor * 0.45)),
    rotation,
  };
}

function unwrapPenRotation(startRotation, endRotation) {
  let adjusted = Number(endRotation) || 0;
  const start = Number(startRotation) || 0;
  while (adjusted - start > Math.PI) adjusted -= Math.PI * 2;
  while (adjusted - start < -Math.PI) adjusted += Math.PI * 2;
  return adjusted;
}

const BRUSH_VERTEX = `
  attribute vec2 a_position;
  uniform vec2 u_resolution;
  varying vec2 v_documentPosition;
  void main() {
    vec2 zeroToOne = a_position / u_resolution;
    vec2 clip = vec2(zeroToOne.x * 2.0 - 1.0, 1.0 - zeroToOne.y * 2.0);
    gl_Position = vec4(clip, 0.0, 1.0);
    v_documentPosition = a_position;
  }
`;

const BRUSH_FRAGMENT = `
  precision highp float;
  uniform vec2 u_start;
  uniform vec2 u_end;
  uniform vec2 u_radiiStart;
  uniform vec2 u_radiiEnd;
  uniform float u_rotationStart;
  uniform float u_rotationEnd;
  uniform float u_pressureStart;
  uniform float u_pressureEnd;
  ${SURFACE_CONTACT_GLSL}
  uniform float u_softness;
  uniform float u_opacity;
  uniform vec3 u_colour;
  uniform sampler2D u_selectionMask;
  uniform float u_selectionEnabled;
  uniform vec2 u_selectionResolution;
  varying vec2 v_documentPosition;

  void main() {
    if (u_selectionEnabled > 0.5) {
      vec2 selectionUv = vec2(v_documentPosition.x / u_selectionResolution.x, 1.0 - v_documentPosition.y / u_selectionResolution.y);
      if (texture2D(u_selectionMask, selectionUv).r < 0.5) discard;
    }
    vec2 segment = u_end - u_start;
    float lengthSquared = dot(segment, segment);
    float t = lengthSquared > 0.0001
      ? clamp(dot(v_documentPosition - u_start, segment) / lengthSquared, 0.0, 1.0)
      : 0.0;
    vec2 closest = mix(u_start, u_end, t);
    vec2 radii = max(vec2(0.5), mix(u_radiiStart, u_radiiEnd, t));
    float rotation = mix(u_rotationStart, u_rotationEnd, t);
    vec2 relative = v_documentPosition - closest;
    float c = cos(-rotation);
    float s = sin(-rotation);
    vec2 local = vec2(
      relative.x * c - relative.y * s,
      relative.x * s + relative.y * c
    );
    float normalizedDistance = length(local / radii);
    float averageRadius = max(0.5, (radii.x + radii.y) * 0.5);
    float softnessNormalized = max(0.02, clamp(u_softness, 0.0, 1.0));
    float inner = max(0.0, 1.0 - softnessNormalized);
    float antiAlias = max(0.75 / averageRadius, 0.002);
    float mask = 1.0 - smoothstep(inner, 1.0 + antiAlias, normalizedDistance);
    // Paper tooth: the tip deposits (or the eraser lifts) where it touches the surface.
    mask *= surfaceContact(v_documentPosition, mix(u_pressureStart, u_pressureEnd, t));
    float alpha = clamp(mask * u_opacity, 0.0, 1.0);
    if (alpha <= 0.001) discard;
    gl_FragColor = vec4(u_colour * alpha, alpha);
  }
`;


const ROUND_CURSOR_FRAGMENT = `
  precision highp float;
  uniform vec2 u_center;
  uniform float u_radius;
  varying vec2 v_documentPosition;

  void main() {
    float edgeDistance = abs(distance(v_documentPosition, u_center) - max(0.5, u_radius));
    float outerMask = 1.0 - smoothstep(2.25, 3.25, edgeDistance);
    float innerMask = 1.0 - smoothstep(0.55, 1.35, edgeDistance);
    float alpha = clamp(outerMask * 0.92, 0.0, 1.0);
    if (alpha <= 0.001) discard;
    vec3 colour = mix(vec3(0.02), vec3(1.0), innerMask);
    gl_FragColor = vec4(colour * alpha, alpha);
  }
`;

const PENCIL_CURSOR_FRAGMENT = `
  precision highp float;
  uniform vec2 u_center;
  uniform vec2 u_radii;
  uniform float u_rotation;
  varying vec2 v_documentPosition;

  void main() {
    vec2 relative = v_documentPosition - u_center;
    float c = cos(-u_rotation);
    float s = sin(-u_rotation);
    vec2 local = vec2(
      relative.x * c - relative.y * s,
      relative.x * s + relative.y * c
    );
    vec2 safeRadii = max(u_radii, vec2(0.5));
    float normalizedDistance = length(local / safeRadii);
    float averageRadius = max(0.5, (safeRadii.x + safeRadii.y) * 0.5);
    float edgeDistance = abs(normalizedDistance - 1.0) * averageRadius;
    float outerMask = 1.0 - smoothstep(2.25, 3.25, edgeDistance);
    float innerMask = 1.0 - smoothstep(0.55, 1.35, edgeDistance);
    float alpha = clamp(outerMask * 0.92, 0.0, 1.0);
    if (alpha <= 0.001) discard;
    vec3 colour = mix(vec3(0.02), vec3(1.0), innerMask);
    gl_FragColor = vec4(colour * alpha, alpha);
  }
`;

// Exported for the pencil-media previews (same shaders as drawing).
export const IMPORTED_VERTEX = `
  attribute vec2 a_position;
  attribute vec2 a_uv;
  uniform vec2 u_resolution;
  varying vec2 v_uv;
  varying vec2 v_documentPosition;
  void main() {
    vec2 zeroToOne = a_position / u_resolution;
    vec2 clip = vec2(zeroToOne.x * 2.0 - 1.0, 1.0 - zeroToOne.y * 2.0);
    gl_Position = vec4(clip, 0.0, 1.0);
    v_uv = a_uv;
    v_documentPosition = a_position;
  }
`;

export const IMPORTED_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_tip;
  uniform vec3 u_colour;
  uniform float u_opacity;
  uniform sampler2D u_selectionMask;
  uniform float u_selectionEnabled;
  uniform vec2 u_selectionResolution;
  varying vec2 v_uv;
  varying vec2 v_documentPosition;
  uniform float u_contactPressure;
  ${SURFACE_CONTACT_GLSL}
  void main() {
    if (u_selectionEnabled > 0.5) {
      vec2 selectionUv = vec2(v_documentPosition.x / u_selectionResolution.x, 1.0 - v_documentPosition.y / u_selectionResolution.y);
      if (texture2D(u_selectionMask, selectionUv).r < 0.5) discard;
    }
    vec4 tip = texture2D(u_tip, v_uv);
    float mask = tip.a * surfaceContact(v_documentPosition, u_contactPressure);
    float alpha = clamp(mask * u_opacity, 0.0, 1.0);
    if (alpha <= 0.001) discard;
    gl_FragColor = vec4(u_colour * alpha, alpha);
  }
`;

// Schema-v2 imported brushes. Dabs are batched (one draw per tip texture
// run) and written into a stroke session with Krita's alpha-darken model:
// coverage moves toward each dab's opacity target by k = mask * flow, so a
// stroke never exceeds its opacity however densely dabs overlap. The
// coverage pass writes the target into R; the optional colour pass writes
// premultiplied colour, used for colour dynamics and colour-image tips.
const IMPORTED_DAB_VERTEX = `
  attribute vec2 a_position;
  attribute vec2 a_uv;
  attribute vec4 a_params;
  attribute vec3 a_colour;
  uniform vec2 u_resolution;
  varying vec2 v_uv;
  varying vec2 v_documentPosition;
  varying vec4 v_params;
  varying vec3 v_colour;
  void main() {
    vec2 zeroToOne = a_position / u_resolution;
    gl_Position = vec4(zeroToOne.x * 2.0 - 1.0, 1.0 - zeroToOne.y * 2.0, 0.0, 1.0);
    v_uv = a_uv;
    v_documentPosition = a_position;
    v_params = a_params;
    v_colour = a_colour;
  }
`;

const IMPORTED_DAB_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_tip;
  uniform sampler2D u_grain;
  uniform float u_grainEnabled;
  uniform float u_grainScale;
  uniform float u_grainDepth;
  uniform float u_grainDepthMinimum;
  uniform float u_grainInvert;
  uniform float u_grainBrightness;
  uniform float u_grainContrast;
  uniform float u_grainRotation;
  uniform float u_grainSubtract;
  uniform float u_tipColour;
  uniform float u_colourPass;
  uniform sampler2D u_selectionMask;
  uniform float u_selectionEnabled;
  uniform vec2 u_selectionResolution;
  varying vec2 v_uv;
  varying vec2 v_documentPosition;
  varying vec4 v_params;
  varying vec3 v_colour;
  uniform float u_contactPressure;
  ${SURFACE_CONTACT_GLSL}
  void main() {
    if (u_selectionEnabled > 0.5) {
      vec2 selectionUv = vec2(v_documentPosition.x / u_selectionResolution.x, 1.0 - v_documentPosition.y / u_selectionResolution.y);
      if (texture2D(u_selectionMask, selectionUv).r < 0.5) discard;
    }
    vec4 tip = texture2D(u_tip, v_uv);
    float k = clamp(tip.a * v_params.x, 0.0, 1.0);
    if (k <= 0.0005) discard;
    // Paper tooth lowers the coverage the dab can reach where the tip does not touch.
    float target = v_params.y * surfaceContact(v_documentPosition, u_contactPressure);
    if (u_grainEnabled > 0.5) {
      vec2 p = v_documentPosition - v_params.zw;
      float c = cos(u_grainRotation);
      float s = sin(u_grainRotation);
      vec2 grainUv = vec2(c * p.x - s * p.y, s * p.x + c * p.y) / max(1.0, u_grainScale);
      float g = texture2D(u_grain, grainUv).r;
      g = clamp((g - 0.5) * (1.0 + u_grainContrast) + 0.5 + u_grainBrightness, 0.0, 1.0);
      if (u_grainInvert > 0.5) g = 1.0 - g;
      float depth = clamp(u_grainDepth, 0.0, 1.0);
      float factor = u_grainSubtract > 0.5 ? clamp(1.0 - depth * (1.0 - g) * 2.0, 0.0, 1.0) : mix(1.0, g, depth);
      target *= max(factor, u_grainDepthMinimum);
    }
    if (u_colourPass > 0.5) {
      vec3 colour = u_tipColour > 0.5 ? tip.rgb : v_colour;
      gl_FragColor = vec4(colour * target * k, k);
    } else {
      gl_FragColor = vec4(target * k, 0.0, 0.0, k);
    }
  }
`;

const IMPORTED_COMPOSITE_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_base;
  uniform sampler2D u_coverage;
  uniform sampler2D u_colourTexture;
  uniform float u_useColourTexture;
  uniform vec3 u_colour;
  uniform float u_strokeOpacity;
  uniform float u_eraser;
  varying vec2 v_uv;
  void main() {
    vec4 base = texture2D(u_base, v_uv);
    float coverage = texture2D(u_coverage, v_uv).r;
    float a = clamp(coverage * u_strokeOpacity, 0.0, 1.0);
    if (u_eraser > 0.5) {
      gl_FragColor = base * (1.0 - a);
      return;
    }
    vec3 colour = u_colour;
    if (u_useColourTexture > 0.5 && coverage > 0.0001) {
      colour = clamp(texture2D(u_colourTexture, v_uv).rgb / coverage, 0.0, 1.0);
    }
    gl_FragColor = vec4(colour * a + base.rgb * (1.0 - a), a + base.a * (1.0 - a));
  }
`;


const AIRBRUSH_VERTEX = `
  attribute vec2 a_position;
  attribute vec2 a_local;
  attribute float a_softness;
  attribute float a_opacity;
  uniform vec2 u_resolution;
  varying vec2 v_local;
  varying float v_softness;
  varying float v_opacity;
  varying vec2 v_documentPosition;

  void main() {
    vec2 zeroToOne = a_position / u_resolution;
    vec2 clip = vec2(zeroToOne.x * 2.0 - 1.0, 1.0 - zeroToOne.y * 2.0);
    gl_Position = vec4(clip, 0.0, 1.0);
    v_local = a_local;
    v_softness = a_softness;
    v_opacity = a_opacity;
    v_documentPosition = a_position;
  }
`;

const AIRBRUSH_FRAGMENT = `
  precision highp float;
  uniform vec3 u_colour;
  uniform sampler2D u_selectionMask;
  uniform float u_selectionEnabled;
  uniform vec2 u_selectionResolution;
  varying vec2 v_local;
  varying float v_softness;
  varying float v_opacity;
  varying vec2 v_documentPosition;
  ${SURFACE_CONTACT_GLSL}

  void main() {
    if (u_selectionEnabled > 0.5) {
      vec2 selectionUv = vec2(v_documentPosition.x / u_selectionResolution.x, 1.0 - v_documentPosition.y / u_selectionResolution.y);
      if (texture2D(u_selectionMask, selectionUv).r < 0.5) discard;
    }
    float distanceFromCentre = length(v_local);
    float softness = clamp(v_softness, 0.0, 1.0);
    float innerRadius = mix(0.92, 0.05, softness);
    float mask = 1.0 - smoothstep(innerRadius, 1.0, distanceFromCentre);
    // Spray lands from above: only a light trace of the paper tooth.
    float alpha = clamp(mask * v_opacity * surfaceContact(v_documentPosition, 1.0), 0.0, 1.0);
    if (alpha <= 0.001) discard;
    gl_FragColor = vec4(u_colour * alpha, alpha);
  }
`;

const QUAD_VERTEX = `
  attribute vec2 a_position;
  varying vec2 v_uv;
  void main() {
    gl_Position = vec4(a_position, 0.0, 1.0);
    v_uv = a_position * 0.5 + 0.5;
  }
`;


const LIQUIFY_FRAGMENT = `
  precision highp float;
  uniform sampler2D u_source;
  uniform sampler2D u_restore;
  uniform vec2 u_resolution;
  uniform vec2 u_center;
  uniform vec2 u_delta;
  uniform float u_radius;
  uniform float u_strength;
  uniform float u_force;
  uniform float u_flow;
  uniform float u_twist;
  uniform float u_chaos;
  uniform float u_detail;
  uniform float u_edge;
  uniform float u_seed;
  uniform float u_mode;
  uniform float u_hasRestore;
  uniform float u_contactPressure;
  ${SURFACE_CONTACT_GLSL}
  uniform sampler2D u_selectionMask;
  uniform float u_selectionEnabled;
  varying vec2 v_uv;

  float hash2(vec2 p) {
    return fract(sin(dot(p, vec2(127.1, 311.7)) + u_seed * 74.7) * 43758.5453123);
  }

  float noise2(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    return mix(mix(hash2(i), hash2(i + vec2(1.0, 0.0)), u.x), mix(hash2(i + vec2(0.0, 1.0)), hash2(i + vec2(1.0, 1.0)), u.x), u.y);
  }

  float fbm(vec2 p) {
    return noise2(p) * 0.58 + noise2(p * 2.03 + vec2(19.7, -11.3)) * 0.29 + noise2(p * 4.11 + vec2(-37.1, 23.9)) * 0.13;
  }

  float smoothFalloff(float t) {
    t = clamp(t, 0.0, 1.0);
    return t * t * (3.0 - 2.0 * t);
  }

  vec2 toUv(vec2 documentPoint) {
    return vec2(documentPoint.x / u_resolution.x, 1.0 - documentPoint.y / u_resolution.y);
  }

  void main() {
    if (u_selectionEnabled > 0.5 && texture2D(u_selectionMask, v_uv).r < 0.5) {
      gl_FragColor = texture2D(u_source, v_uv);
      return;
    }
    vec2 world = vec2(v_uv.x * u_resolution.x, (1.0 - v_uv.y) * u_resolution.y);
    vec2 local = world - u_center;
    float distanceFromCenter = length(local);
    vec4 original = texture2D(u_source, v_uv);
    if (distanceFromCenter > u_radius) {
      gl_FragColor = original;
      return;
    }

    float rawFalloff = smoothFalloff(1.0 - distanceFromCenter / max(1.0, u_radius));
    float curve = max(0.18, 1.8 - clamp(u_edge, 0.0, 1.0) * 1.6);
    float falloff = pow(rawFalloff, curve);
    float forceScale = 0.25 + clamp(u_force, 0.0, 1.0) * 1.5;
    float flowScale = 0.25 + clamp(u_flow, 0.0, 1.0) * 1.5;
    float twistScale = 0.25 + clamp(u_twist, 0.0, 1.0) * 1.5;
    float chaosScale = clamp(u_chaos, 0.0, 1.0) * 2.0;
    float detailScale = 0.5 + clamp(u_detail, 0.0, 1.0);
    float power = u_strength * falloff * forceScale;
    float dragDistance = length(u_delta);
    vec2 normal = distanceFromCenter > 0.001 ? local / distanceFromCenter : vec2(0.0);
    vec2 offset = vec2(0.0);

    if (u_mode < 1.5) {
      offset = u_delta * power * flowScale;
    } else if (u_mode < 2.5) {
      float direction = u_mode < 2.0 ? 1.0 : -1.0;
      float angle = direction * power * twistScale * min(1.2, 0.18 + dragDistance * 0.035);
      float c = cos(angle);
      float si = sin(angle);
      offset = vec2(local.x * c - local.y * si, local.x * si + local.y * c) - local;
    } else if (u_mode < 3.5) {
      offset = normal * (u_radius * power * -0.42);
    } else if (u_mode < 4.5) {
      offset = normal * (u_radius * power * 0.42);
    } else if (u_mode < 5.5) {
      float n = fbm(world * 0.018 * detailScale);
      float fine = fbm(world * 0.0558 * detailScale + vec2(7.0));
      float baseAngle = atan(local.y, local.x);
      float count = 6.0 + floor(n * 10.0 + 0.5);
      float stepAngle = 6.28318530718 / max(1.0, count);
      float snapped = floor((baseAngle + (fine - 0.5) * 1.8 * chaosScale) / stepAngle + 0.5) * stepAngle;
      float fracture = pow(abs(n * 2.0 - 1.0), 0.42);
      float amount = dragDistance * power * (0.45 + fracture * 1.45);
      offset = vec2(cos(snapped), sin(snapped)) * amount + u_delta * power * 0.18 * smoothFalloff(distanceFromCenter / max(1.0, u_radius));
    } else if (u_mode < 6.5) {
      float n1 = fbm(world * 0.026 * detailScale);
      float n2 = fbm((world + vec2(91.7, -47.2)) * 0.026 * detailScale);
      float branch = sin(n1 * 18.0 + atan(local.y, local.x) * 5.0);
      float vein = pow(1.0 - abs(branch), 3.0);
      float angle = atan(n2 - 0.5, n1 - 0.5) + branch * 0.8;
      offset = vec2(cos(angle), sin(angle)) * (u_radius * power * vein * 0.38) + u_delta * power * 0.18;
    } else if (u_mode < 7.5) {
      float angle = atan(u_delta.y + 0.001, u_delta.x + 0.001) + 1.57079632679;
      float lineNoise = fbm(world * 0.015 * detailScale);
      float fault = local.x * cos(angle) + local.y * sin(angle) + (lineNoise - 0.5) * u_radius * 0.9;
      float side = fault >= 0.0 ? 1.0 : -1.0;
      float crack = exp(-abs(fault) / max(1.0, u_radius * 0.12));
      offset = u_delta * power * (0.28 + crack * 1.8) * side;
    } else if (u_mode < 8.5) {
      float inward = u_radius * power * 0.34 * (1.0 - distanceFromCenter / max(1.0, u_radius));
      vec2 orbit = vec2(-normal.y, normal.x);
      offset = -normal * inward + orbit * (u_radius * power * 0.16 * twistScale);
    } else if (u_mode < 9.5) {
      float wave = sin(distanceFromCenter * 0.19 * detailScale - dragDistance * 0.7);
      offset = normal * (wave * u_radius * power * 0.18 * (0.55 + chaosScale * 0.45)) + u_delta * power * 0.12;
    } else if (u_mode < 10.5) {
      float depth = clamp((world.y / max(1.0, u_resolution.y) - 0.25) / 0.75, 0.0, 1.0);
      float perspective = pow(depth, 2.2);
      float wave = sin(world.y * 0.055 + u_seed * 0.133 + (fbm(world * vec2(0.012, 0.045)) - 0.5) * 2.8);
      float ripple = sin(world.x * (0.018 + perspective * 0.012) + world.y * 0.02 + u_seed * 0.071);
      float horizontalSign = u_delta.x < 0.0 ? -1.0 : 1.0;
      offset.x = horizontalSign * ((dragDistance * power * wave * (0.65 + perspective * 1.55)) + (u_radius * power * ripple * (0.018 + perspective * 0.038))) * perspective;
      offset.y = u_radius * power * 0.01 * (fbm(world * 0.035) - 0.5) * pow(depth, 2.85);
    } else if (u_mode < 11.5) {
      float ring = smoothFalloff(1.0 - abs(distanceFromCenter / max(1.0, u_radius) - (0.58 + u_edge * 0.32)) / 0.34);
      offset = u_delta * u_strength * ring * flowScale * forceScale;
    } else if (u_mode < 12.5) {
      if (u_hasRestore > 0.5) {
        vec4 restored = texture2D(u_restore, v_uv);
        gl_FragColor = mix(original, restored, clamp(u_strength * rawFalloff, 0.0, 1.0));
        return;
      }
    } else if (u_mode < 13.5) {
      offset = u_delta * power * flowScale * 0.34;
    } else {
      float turbulence = (fbm(world * 0.018 * detailScale) - 0.5) * chaosScale;
      float angle = atan(u_delta.y + 0.001, u_delta.x + 0.001) + turbulence * 0.9;
      float amount = power * dragDistance * (0.32 * flowScale + 1.0);
      offset = vec2(cos(angle), sin(angle)) * amount;
    }

    // Smudge drags material where the blender touches the relief; valleys move
    // little per pass (only set for the Smudge mode; other modes have no tooth).
    offset *= surfaceContact(world, u_contactPressure);
    vec2 samplePoint = clamp(world - offset, vec2(0.0), u_resolution - vec2(1.0));
    gl_FragColor = texture2D(u_source, toUv(samplePoint));
  }
`;

const PAINT_FRAGMENT = `
  precision highp float;
  uniform vec2 u_start;
  uniform vec2 u_end;
  uniform float u_radius;
  uniform float u_opacity;
  uniform vec3 u_colour;
  uniform float u_tool;
  uniform float u_view;
  uniform float u_bristleCount;
  uniform float u_bristleThickness;
  uniform float u_pressure;
  uniform float u_stickiness;
  uniform float u_gloss;
  uniform float u_lightAngle;
  uniform float u_lightElevation;
  uniform sampler2D u_selectionMask;
  uniform float u_selectionEnabled;
  uniform vec2 u_selectionResolution;
  varying vec2 v_documentPosition;

  void main() {
    if (u_selectionEnabled > 0.5) {
      vec2 selectionUv = vec2(v_documentPosition.x / u_selectionResolution.x, 1.0 - v_documentPosition.y / u_selectionResolution.y);
      if (texture2D(u_selectionMask, selectionUv).r < 0.5) discard;
    }

    vec2 segment = u_end - u_start;
    float segmentLengthSquared = max(dot(segment, segment), 0.000001);
    float t = clamp(dot(v_documentPosition - u_start, segment) / segmentLengthSquared, 0.0, 1.0);
    vec2 closest = u_start + segment * t;
    vec2 relative = v_documentPosition - closest;
    float distanceFromCentre = length(relative);
    float radius = max(0.5, u_radius);
    float normalizedDistance = distanceFromCentre / radius;
    float antiAlias = max(0.7 / radius, 0.002);
    float mask = 1.0 - smoothstep(1.0 - antiAlias, 1.0 + antiAlias, normalizedDistance);
    float heightRatio = sqrt(max(0.0, 1.0 - normalizedDistance * normalizedDistance));

    if (u_tool > 0.5 && u_tool < 1.5) {
      vec2 direction = normalize(abs(segment.x) + abs(segment.y) > 0.0001 ? segment : vec2(1.0, 0.0));
      vec2 normalDirection = vec2(-direction.y, direction.x);
      float crossPosition = dot(relative, normalDirection) / radius;
      float bristles = max(1.0, u_bristleCount);
      float wave = abs(sin((crossPosition * 0.5 + 0.5) * bristles * 3.14159265359));
      float threshold = mix(0.82, 0.08, clamp(u_bristleThickness, 0.01, 1.0));
      float strand = smoothstep(threshold, 1.0, wave);
      float edge = 1.0 - smoothstep(0.72, 1.0, normalizedDistance);
      mask *= mix(0.12, 1.0, strand) * edge;
      heightRatio *= 0.18 + 0.42 * clamp(u_pressure, 0.01, 1.0);
    } else if (u_tool > 1.5) {
      float hardBand = 1.0 - smoothstep(0.84, 1.0, normalizedDistance);
      float scrapeLines = 0.76 + 0.24 * sin((t * 37.0 + normalizedDistance * 11.0) * 3.14159265359);
      mask *= hardBand * scrapeLines;
      heightRatio = mix(0.025, 0.11, clamp(u_stickiness, 0.0, 1.0));
    }

    float alpha = clamp(mask * u_opacity * u_pressure, 0.0, 1.0);
    if (alpha <= 0.001) discard;

    vec3 outputColour = u_colour;
    if (u_view > 1.5) {
      float heightUnits = clamp(radius * heightRatio, 0.0, 4000.0);
      float grey = clamp(heightUnits / 4000.0, 0.0, 1.0);
      outputColour = vec3(grey);
    } else if (u_view < 0.5) {
      vec2 radial = distanceFromCentre > 0.0001 ? relative / radius : vec2(0.0);
      vec3 normal = normalize(vec3(radial.x, radial.y, max(0.06, heightRatio)));
      float angle = radians(u_lightAngle);
      float elevation = radians(clamp(u_lightElevation, 1.0, 60.0));
      vec3 light = normalize(vec3(cos(angle) * cos(elevation), sin(angle) * cos(elevation), sin(elevation)));
      float diffuse = 0.50 + max(0.0, dot(normal, light)) * 0.50;
      vec3 halfVector = normalize(light + vec3(0.0, 0.0, 1.0));
      float gloss = clamp(u_gloss, 0.0, 1.0);
      float specular = pow(max(0.0, dot(normal, halfVector)), mix(8.0, 180.0, gloss)) * gloss * 0.85;
      outputColour = clamp(u_colour * diffuse + vec3(specular), 0.0, 1.0);
    }

    gl_FragColor = vec4(outputColour * alpha, alpha);
  }
`;

const DISPLAY_FRAGMENT = `
  precision mediump float;
  uniform sampler2D u_texture;
  uniform float u_forceOpaque;
  uniform float u_opacity;
  varying vec2 v_uv;
  void main() {
    vec4 colour = texture2D(u_texture, v_uv) * u_opacity;
    if (u_forceOpaque > 0.5) {
      gl_FragColor = vec4(colour.rgb + vec3(1.0 - colour.a), 1.0);
    } else {
      gl_FragColor = colour;
    }
  }
`;

export function createCanvasGpuEngine(canvas, width, height) {
  if (!canvas || typeof canvas.getContext !== 'function') {
    throw new Error('A canvas element is required for the GPU renderer.');
  }

  const gl = canvas.getContext('webgl', {
    alpha: true,
    antialias: false,
    depth: false,
    stencil: false,
    premultipliedAlpha: true,
    preserveDrawingBuffer: true,
    powerPreference: 'high-performance',
  }) || canvas.getContext('experimental-webgl', {
    alpha: true,
    antialias: false,
    depth: false,
    stencil: false,
    premultipliedAlpha: true,
    preserveDrawingBuffer: true,
    powerPreference: 'high-performance',
  });

  if (!gl) throw new Error('WebGL is unavailable.');

  let documentWidth = Math.max(1, Math.floor(Number(width) || 1));
  let documentHeight = Math.max(1, Math.floor(Number(height) || 1));
  let documentTexture = null;
  let documentFramebuffer = null;
  const layerTargets = new Map();
  let layerState = [{ id: 'layer-1', name: 'Layer 1', visible: true, opacity: 1 }];
  let activeLayerId = 'layer-1';
  let destroyed = false;
  let containsPixels = false;
  let importedCursor = null;
  let roundCursor = null;
  let pencilCursor = null;
  let selectionMaskTexture = null;
  let selectionMaskEnabled = false;
  let selectionMaskKey = '';
  let oilPaintSystem = null;
  let paintDisplayViewMode = 0;
  // One non-accumulating round-brush session per layer. A single shared
  // session let a collaborator's stroke_start destroy the local stroke that
  // was still in progress.
  const roundBrushStrokeSessions = new Map();
  let scheduledRenderFrame = 0;
  const blendMinMaxExtension = gl.getExtension('EXT_blend_minmax');

  // getUniformLocation/getAttribLocation allocate on every call. Brush
  // drawing queries them per segment, so cache them per program.
  const uniformLocationCache = new Map();
  const attribLocationCache = new Map();
  function uniformLocation(program, name) {
    let locations = uniformLocationCache.get(program);
    if (!locations) {
      locations = new Map();
      uniformLocationCache.set(program, locations);
    }
    if (!locations.has(name)) locations.set(name, gl.getUniformLocation(program, name));
    return locations.get(name);
  }
  function attribLocation(program, name) {
    let locations = attribLocationCache.get(program);
    if (!locations) {
      locations = new Map();
      attribLocationCache.set(program, locations);
    }
    if (!locations.has(name)) locations.set(name, gl.getAttribLocation(program, name));
    return locations.get(name);
  }

  const brushProgram = createProgram(gl, BRUSH_VERTEX, BRUSH_FRAGMENT);
  const paintProgram = createProgram(gl, BRUSH_VERTEX, PAINT_FRAGMENT);
  const roundCursorProgram = createProgram(gl, BRUSH_VERTEX, ROUND_CURSOR_FRAGMENT);
  const pencilCursorProgram = createProgram(gl, BRUSH_VERTEX, PENCIL_CURSOR_FRAGMENT);
  const displayProgram = createProgram(gl, QUAD_VERTEX, DISPLAY_FRAGMENT);
  const importedProgram = createProgram(gl, IMPORTED_VERTEX, IMPORTED_FRAGMENT);
  const importedDabProgram = createProgram(gl, IMPORTED_DAB_VERTEX, IMPORTED_DAB_FRAGMENT);
  const importedCompositeProgram = createProgram(gl, QUAD_VERTEX, IMPORTED_COMPOSITE_FRAGMENT);
  const airbrushProgram = createProgram(gl, AIRBRUSH_VERTEX, AIRBRUSH_FRAGMENT);
  const liquifyProgram = createProgram(gl, QUAD_VERTEX, LIQUIFY_FRAGMENT);
  // Pencil media other than graphite (dryMedia.js).
  // A shader failure here must never break the canvas: fall back to graphite.
  const dryMedia = (() => {
    try { return createDryMediaRenderer(gl, createProgram); } catch (error) { console.error('[pencil media] dry media shader unavailable', error); return null; }
  })();
  let dryMediaStroke = null;
  let liquifyScratchTexture = null;
  let liquifyScratchFramebuffer = null;
  let liquifyRestoreTexture = null;
  let liquifyRestoreFramebuffer = null;
  let layerMoveSession = null;
  const brushBuffer = gl.createBuffer();
  const quadBuffer = gl.createBuffer();
  const importedBuffer = gl.createBuffer();
  const importedDabBuffer = gl.createBuffer();
  const airbrushBuffer = gl.createBuffer();
  const importedTipTextureCache = new WeakMap();
  const importedTipTextures = new Set();

  if (!brushBuffer || !quadBuffer || !importedBuffer || !airbrushBuffer) throw new Error('Unable to create GPU buffers.');

  const oilPaintOptions = {
    getSelection: () => ({
      enabled: selectionMaskEnabled && Boolean(selectionMaskTexture),
      texture: selectionMaskTexture,
    }),
    requestRender: () => requestRender(),
  };
  // With the physical paint flag and a ready WebGPU device, the Paint Tool uses
  // the physical engine; layers painted with the original oil system are
  // delegated to it, so existing artwork is unaffected.
  const physicalPaintGpu = getReadyPaintDevice();
  oilPaintSystem = physicalPaintGpu
    ? createPhysicalPaintSystem(gl, documentWidth, documentHeight, {
      gpu: physicalPaintGpu,
      requestRender: () => requestRender(),
      createLegacySystem: () => createOilPaintGpuSystem(gl, documentWidth, documentHeight, oilPaintOptions),
      onSurfaceChange: (surface) => {
        // Lets the Paint Tool's Surface section reflect a surface loaded with a document.
        window.dispatchEvent(new CustomEvent('vx-paint-surface', { detail: surface }));
        // Drawing tools (and their previews) follow the new paper at once.
        refreshTooth();
      },
    })
    : createOilPaintGpuSystem(gl, documentWidth, documentHeight, oilPaintOptions);
  // Publish the starting paper for the tool previews (after the core is set up).
  Promise.resolve().then(() => { if (!destroyed) refreshTooth(); });

  gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
  gl.bufferData(
    gl.ARRAY_BUFFER,
    new Float32Array([-1, -1, 1, -1, -1, 1, -1, 1, 1, -1, 1, 1]),
    gl.STATIC_DRAW,
  );

  function ensureSelectionMaskTexture() {
    if (selectionMaskTexture) return selectionMaskTexture;
    selectionMaskTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, selectionMaskTexture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);
    return selectionMaskTexture;
  }

  function getSelectionMaskKey(selection) {
    const bounds = selection.bounds || {};
    const vertices = Array.isArray(selection.vertices)
      ? selection.vertices.map((vertex) => `${Number(vertex && vertex.x) || 0},${Number(vertex && vertex.y) || 0}`).join(';')
      : '';
    return [
      documentWidth,
      documentHeight,
      selection.shape || '',
      selection.inverted ? 1 : 0,
      Number(bounds.x) || 0,
      Number(bounds.y) || 0,
      Number(bounds.width) || 0,
      Number(bounds.height) || 0,
      vertices,
    ].join('|');
  }

  function setSelectionMask(selection) {
    if (!selection || !selection.bounds) {
      if (selectionMaskEnabled) requestRender();
      selectionMaskEnabled = false;
      return;
    }
    // Remote strokes toggle between the sender's mask and the local one for
    // every live event. Rebuilding a document-sized mask each time was a
    // full-canvas rasterise and upload per remote point.
    const maskKey = getSelectionMaskKey(selection);
    if (selectionMaskTexture && maskKey === selectionMaskKey) {
      selectionMaskEnabled = true;
      return;
    }
    selectionMaskKey = maskKey;
    const maskCanvas = document.createElement('canvas');
    maskCanvas.width = documentWidth;
    maskCanvas.height = documentHeight;
    const context = maskCanvas.getContext('2d');
    if (!context) return;
    context.clearRect(0, 0, documentWidth, documentHeight);
    if (selection.inverted) {
      context.fillStyle = '#ffffff';
      context.fillRect(0, 0, documentWidth, documentHeight);
      context.fillStyle = '#000000';
    } else {
      context.fillStyle = '#ffffff';
    }
    context.beginPath();
    if (selection.shape === 'ellipse') {
      const bounds = selection.bounds;
      context.ellipse(bounds.x + bounds.width / 2, bounds.y + bounds.height / 2, Math.max(0.5, bounds.width / 2), Math.max(0.5, bounds.height / 2), 0, 0, Math.PI * 2);
    } else if (selection.shape === 'polygon' && Array.isArray(selection.vertices) && selection.vertices.length >= 3) {
      context.moveTo(selection.vertices[0].x, selection.vertices[0].y);
      for (let index = 1; index < selection.vertices.length; index += 1) context.lineTo(selection.vertices[index].x, selection.vertices[index].y);
      context.closePath();
    } else {
      const bounds = selection.bounds;
      context.rect(bounds.x, bounds.y, bounds.width, bounds.height);
    }
    context.fill();
    const texture = ensureSelectionMaskTexture();
    gl.activeTexture(gl.TEXTURE7);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, maskCanvas);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE0);
    selectionMaskEnabled = true;
    requestRender();
  }

  function applySelectionUniforms(program) {
    const enabledLocation = uniformLocation(program, 'u_selectionEnabled');
    if (enabledLocation !== null) gl.uniform1f(enabledLocation, selectionMaskEnabled && selectionMaskTexture ? 1 : 0);
    const resolutionLocation = uniformLocation(program, 'u_selectionResolution');
    if (resolutionLocation !== null) gl.uniform2f(resolutionLocation, documentWidth, documentHeight);
    const maskLocation = uniformLocation(program, 'u_selectionMask');
    if (maskLocation !== null && selectionMaskTexture) {
      gl.activeTexture(gl.TEXTURE7);
      gl.bindTexture(gl.TEXTURE_2D, selectionMaskTexture);
      gl.uniform1i(maskLocation, 7);
      gl.activeTexture(gl.TEXTURE0);
    }
  }

  // ---------------------------------------------------------------- paper tooth
  // Drawing tools touch the document paper (surfaceContact.js). The tooth
  // texture is the paint system's own physical height field, normalised, and
  // is re-uploaded only when the paper changes; without the physical paint
  // system there is no paper and the tools draw as before.
  let toothTexture = null;
  let toothKey = '';
  let toothStrength = 0;
  let toothAbsorbency = 0;
  // Calibrated papers: fine repeating tooth tile (PAPER_TOOTH_TILE px) and the
  // paper's calibration; canvases, panels and digital: the paint relief as is.
  let toothCalibration = null;
  let toothTile = [1, 1];
  function refreshTooth() {
    const field = oilPaintSystem && typeof oilPaintSystem.getToothField === 'function' ? oilPaintSystem.getToothField() : null;
    const key = field && field.heightField ? field.key : '';
    if (key === toothKey) return;
    toothKey = key;
    if (!key) {
      toothStrength = 0;
      toothAbsorbency = 0;
      toothCalibration = null;
      publishToothField(null);
      return;
    }
    const physics = normaliseToothField(field.heightField);
    const { sd } = physics;
    const calibration = sd > 1e-5 ? paperCalibration(field.preset) : null;
    const tile = calibration ? paperToothTile(field.preset, field.textureScale) : null;
    const bytes = tile ? tile.bytes : physics.bytes;
    const texWidth = tile ? tile.width : field.width;
    const texHeight = tile ? tile.height : field.height;
    toothCalibration = tile ? calibration : null;
    toothTile = tile ? [tile.width, tile.height] : [documentWidth, documentHeight];
    if (!toothTexture) toothTexture = gl.createTexture();
    gl.bindTexture(gl.TEXTURE_2D, toothTexture);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.LUMINANCE, texWidth, texHeight, 0, gl.LUMINANCE, gl.UNSIGNED_BYTE, bytes);
    gl.pixelStorei(gl.UNPACK_ALIGNMENT, 4);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    // The paper tile repeats (power of two, so WebGL 1 can wrap it).
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, tile ? gl.REPEAT : gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, tile ? gl.REPEAT : gl.CLAMP_TO_EDGE);
    gl.bindTexture(gl.TEXTURE_2D, null);
    toothStrength = drawingToothStrength({ tooth: field.tooth, friction: field.friction, sd }, toothCalibration);
    toothAbsorbency = Math.max(0, Number(field.absorbency) || 0);
    publishToothField({
      bytes,
      width: texWidth,
      height: texHeight,
      strength: toothStrength,
      absorbency: toothAbsorbency,
      key,
      calibration: toothCalibration,
      repeat: Boolean(tile),
      pxPerCell: tile ? 1 : documentWidth / Math.max(1, field.width),
    });
  }

  // kind: 'pen' (round pen, calligraphy, shapes, eraser), 'pencil', 'airbrush',
  // 'imported', 'liquify'. Returns the profile with the effective strength.
  function applySurfaceContact(program, kind, options = {}) {
    refreshTooth();
    const orientation = options.pencilOrientation && typeof options.pencilOrientation === 'object' ? options.pencilOrientation : null;
    const elevation = clamp(Number(orientation && orientation.elevation) || 90, 15, 90);
    const profile = calibrateProfile(contactProfileFor({
      tool: options.isEraser ? 'eraser' : kind === 'liquify' ? 'liquify' : 'pen',
      preset: kind === 'pencil' ? 'pencil' : kind === 'airbrush' ? 'airbrush' : 'pen',
      imported: kind === 'imported',
      media: options.surfaceMedia || getSurfaceMedia(),
      grade: (Number(options.pencilGrade) || 0) / 8,
      tilt: (90 - elevation) / 75,
      liquifyMode: String(options.liquifyMode || options.distortionMode || ''),
    }), toothCalibration);
    const strength = toothTexture ? clamp(toothStrength * profile.sensitivity, 0, 1) : 0;
    gl.uniform1f(uniformLocation(program, 'u_toothStrength'), strength);
    // Document px covered by one copy of the tooth texture.
    gl.uniform2f(uniformLocation(program, 'u_toothResolution'), toothTile[0], toothTile[1]);
    gl.uniform4f(uniformLocation(program, 'u_contact'), profile.base, profile.pressureReach, Math.max(0.05, profile.width), profile.floor);
    const toothLocation = uniformLocation(program, 'u_tooth');
    if (toothLocation !== null && toothTexture) {
      gl.activeTexture(gl.TEXTURE6);
      gl.bindTexture(gl.TEXTURE_2D, toothTexture);
      gl.uniform1i(toothLocation, 6);
      gl.activeTexture(gl.TEXTURE0);
    }
    return { ...profile, strength, absorbency: toothAbsorbency };
  }

  function disableSurfaceContact(program) {
    gl.uniform1f(uniformLocation(program, 'u_toothStrength'), 0);
  }

  function deleteLayerTarget(target) {
    if (!target) return;
    if (target.framebuffer) gl.deleteFramebuffer(target.framebuffer);
    if (target.texture) gl.deleteTexture(target.texture);
    if (oilPaintSystem && target.id) oilPaintSystem.deleteLayer(target.id);
  }

  function createLayerTarget(id) {
    const texture = createTexture(gl, documentWidth, documentHeight);
    const framebuffer = createFramebuffer(gl, texture);
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    const target = { id, texture, framebuffer, containsPixels: false };
    layerTargets.set(id, target);
    if (oilPaintSystem) oilPaintSystem.ensureLayer(id);
    return target;
  }

  function syncActiveTarget() {
    let target = layerTargets.get(activeLayerId);
    if (!target) target = createLayerTarget(activeLayerId);
    documentTexture = target.texture;
    documentFramebuffer = target.framebuffer;
    containsPixels = target.containsPixels;
    return target;
  }

  function deleteDocumentTarget() {
    for (const target of layerTargets.values()) deleteLayerTarget(target);
    layerTargets.clear();
    documentFramebuffer = null;
    documentTexture = null;
  }

  function createDocumentTarget() {
    destroyAllRoundBrushStrokeSessions();
    deleteDocumentTarget();
    for (const layer of layerState) createLayerTarget(layer.id);
    syncActiveTarget();
  }

  function commitActivePixels(value) {
    const target = layerTargets.get(activeLayerId);
    if (target) target.containsPixels = Boolean(value);
    containsPixels = Boolean(value);
  }

  function drawTexture(texture, framebuffer, viewportWidth, viewportHeight, forceOpaque, opacity = 1, blend = false) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.viewport(0, 0, viewportWidth, viewportHeight);
    if (blend) {
      gl.enable(gl.BLEND);
      gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    } else {
      gl.disable(gl.BLEND);
    }
    gl.useProgram(displayProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    const position = attribLocation(displayProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1i(uniformLocation(displayProgram, 'u_texture'), 0);
    gl.uniform1f(uniformLocation(displayProgram, 'u_forceOpaque'), forceOpaque ? 1 : 0);
    gl.uniform1f(uniformLocation(displayProgram, 'u_opacity'), clamp(opacity, 0, 1));
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.bindTexture(gl.TEXTURE_2D, null);
    if (blend) gl.disable(gl.BLEND);
  }

  function drawRoundCursor() {
    if (!roundCursor || !roundCursor.visible || !roundCursor.point) return;

    const point = roundCursor.point;
    const size = Math.max(1, Number(roundCursor.size) || 1);
    const radius = Math.max(0.5, size * 0.5);
    const padding = 4;
    const minimumX = point.x - radius - padding;
    const minimumY = point.y - radius - padding;
    const maximumX = point.x + radius + padding;
    const maximumY = point.y + radius + padding;
    const vertices = new Float32Array([
      minimumX, minimumY,
      maximumX, minimumY,
      minimumX, maximumY,
      minimumX, maximumY,
      maximumX, minimumY,
      maximumX, maximumY,
    ]);

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(roundCursorProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, brushBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
    const position = attribLocation(roundCursorProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(uniformLocation(roundCursorProgram, 'u_resolution'), documentWidth, documentHeight);
    gl.uniform2f(uniformLocation(roundCursorProgram, 'u_center'), point.x, point.y);
    gl.uniform1f(uniformLocation(roundCursorProgram, 'u_radius'), radius);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.BLEND);
  }

  function drawPencilCursor() {
    if (!pencilCursor || !pencilCursor.visible || !pencilCursor.point) return;

    const point = pencilCursor.point;
    const width = Math.max(1, Number(pencilCursor.width) || 1);
    const height = Math.max(1, Number(pencilCursor.height) || 1);
    const rotation = Number(pencilCursor.rotation) || 0;
    const radiusX = width * 0.5;
    const radiusY = height * 0.5;
    const padding = 5;
    const extentX = Math.abs(Math.cos(rotation)) * radiusX + Math.abs(Math.sin(rotation)) * radiusY;
    const extentY = Math.abs(Math.sin(rotation)) * radiusX + Math.abs(Math.cos(rotation)) * radiusY;
    const minimumX = point.x - extentX - padding;
    const minimumY = point.y - extentY - padding;
    const maximumX = point.x + extentX + padding;
    const maximumY = point.y + extentY + padding;
    const vertices = new Float32Array([
      minimumX, minimumY,
      maximumX, minimumY,
      minimumX, maximumY,
      minimumX, maximumY,
      maximumX, minimumY,
      maximumX, maximumY,
    ]);

    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(pencilCursorProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, brushBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
    const position = attribLocation(pencilCursorProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.uniform2f(uniformLocation(pencilCursorProgram, 'u_resolution'), documentWidth, documentHeight);
    gl.uniform2f(uniformLocation(pencilCursorProgram, 'u_center'), point.x, point.y);
    gl.uniform2f(uniformLocation(pencilCursorProgram, 'u_radii'), radiusX, radiusY);
    gl.uniform1f(uniformLocation(pencilCursorProgram, 'u_rotation'), rotation);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.BLEND);
  }

  function drawImportedCursor() {
    if (!importedCursor || !importedCursor.visible || !importedCursor.brush) return;
    const { point, brush, options } = importedCursor;
    if (!point) return;

    const dab = {
      seed: importedCursor.seed || 1,
      tipIndex: importedCursor.seed || 1,
      sizeMultiplier: 1,
      rotation: Number(importedCursor.rotation) || 0,
      opacity: 1,
      x: Number(point.x) || 0,
      y: Number(point.y) || 0,
    };
    const tip = createImportedTipTexture(brush, dab, options || {});
    if (!tip) return;

    const size = Math.max(0.5, (Number(options && options.size) || 1) * Math.max(0.05, Number(brush.tipScale) || 1));
    const sourceMaximum = Math.max(tip.width, tip.height);
    const halfWidth = (size * (tip.width / sourceMaximum)) * 0.5;
    const halfHeight = (size * (tip.height / sourceMaximum)) * 0.5;
    const angle = Number(importedCursor.rotation) || 0;
    const c = Math.cos(angle);
    const si = Math.sin(angle);
    const corners = [
      [-halfWidth, -halfHeight, 0, 0],
      [halfWidth, -halfHeight, 1, 0],
      [-halfWidth, halfHeight, 0, 1],
      [-halfWidth, halfHeight, 0, 1],
      [halfWidth, -halfHeight, 1, 0],
      [halfWidth, halfHeight, 1, 1],
    ];
    const vertices = [];
    for (const [x, y, u, v] of corners) {
      vertices.push(
        dab.x + x * c - y * si,
        dab.y + x * si + y * c,
        u,
        v,
      );
    }

    const colour = hexToColor(options && options.color ? options.color : '#111111');
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(importedProgram);
    applySelectionUniforms(importedProgram);
    // The cursor is drawn with the dab program: never shaped by the paper.
    disableSurfaceContact(importedProgram);
    gl.uniform2f(uniformLocation(importedProgram, 'u_resolution'), documentWidth, documentHeight);
    gl.uniform3f(uniformLocation(importedProgram, 'u_colour'), colour[0], colour[1], colour[2]);
    gl.uniform1f(uniformLocation(importedProgram, 'u_opacity'), 0.38);
    gl.bindBuffer(gl.ARRAY_BUFFER, importedBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STREAM_DRAW);
    const position = attribLocation(importedProgram, 'a_position');
    const uv = attribLocation(importedProgram, 'a_uv');
    gl.enableVertexAttribArray(position);
    gl.enableVertexAttribArray(uv);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 16, 0);
    gl.vertexAttribPointer(uv, 2, gl.FLOAT, false, 16, 8);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, tip.texture);
    gl.uniform1i(uniformLocation(importedProgram, 'u_tip'), 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disableVertexAttribArray(uv);
    gl.disable(gl.BLEND);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  function cancelScheduledRender() {
    if (!scheduledRenderFrame) return;
    if (typeof window !== 'undefined' && typeof window.cancelAnimationFrame === 'function') {
      window.cancelAnimationFrame(scheduledRenderFrame);
    }
    scheduledRenderFrame = 0;
  }

  // Drawing calls only mark the screen dirty. The browser presents at most
  // once per frame anyway, so compositing every layer after every segment of
  // every coalesced pointer sample was pure overhead that grew with layer
  // count and document size. Pointer events are dispatched before animation
  // frame callbacks, so this adds no latency.
  function requestRender() {
    if (destroyed) return;
    if (typeof window === 'undefined' || typeof window.requestAnimationFrame !== 'function') {
      renderNow();
      return;
    }
    if (scheduledRenderFrame) return;
    scheduledRenderFrame = window.requestAnimationFrame(() => {
      scheduledRenderFrame = 0;
      renderNow();
    });
  }

  // Public render stays synchronous: export paths render and then read the
  // canvas in the same task.
  // Drawing games composite onto transparent paper so a picture holds only its own ink and pictures
  // underneath (another player's part, an onion skin) show through. Canvas documents keep white paper.
  let transparentPaper = false;
  function setTransparentPaper(value) {
    const next = Boolean(value);
    if (next === transparentPaper) return;
    transparentPaper = next;
    requestRender();
  }

  function render(includeCursor = true, paintViewModeOverride = null) {
    cancelScheduledRender();
    renderNow(includeCursor, paintViewModeOverride);
  }

  function renderNow(includeCursor = true, paintViewModeOverride = null) {
    if (destroyed) return;
    flushRoundBrushStrokeSessions();
    const paintViewMode = Number.isFinite(Number(paintViewModeOverride))
      ? Number(paintViewModeOverride)
      : paintDisplayViewMode;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.disable(gl.BLEND);
    const heightmapView = paintViewMode === 2;
    const paper = heightmapView || transparentPaper ? 0 : 1;
    gl.clearColor(paper, paper, paper, transparentPaper && !heightmapView ? 0 : 1);
    gl.clear(gl.COLOR_BUFFER_BIT);
    // The document's painting surface (physical paint engine) replaces plain white paper.
    if (paper && !transparentPaper && oilPaintSystem && typeof oilPaintSystem.renderSurface === 'function') {
      oilPaintSystem.renderSurface(null, canvas.width, canvas.height);
    }
    for (const layer of layerState) {
      if (!layer || layer.visible === false) continue;
      const target = layerTargets.get(layer.id);
      if (!target || !target.texture) continue;
      const layerOpacity = layer.opacity == null ? 1 : layer.opacity;
      if (paintViewMode === 0) {
        drawTexture(target.texture, null, canvas.width, canvas.height, false, layerOpacity, true);
      }
      if (oilPaintSystem) oilPaintSystem.renderLayer(layer.id, null, canvas.width, canvas.height, layerOpacity, paintViewMode);
    }
    if (includeCursor) {
      drawRoundCursor();
      drawPencilCursor();
      drawImportedCursor();
    }
  }

  function renderBaseLayer(layerId = activeLayerId, includeCursor = false) {
    if (destroyed) return;
    flushRoundBrushStrokeSessions();
    const target = layerTargets.get(layerId);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (target && target.texture) drawTexture(target.texture, null, canvas.width, canvas.height, false, 1, true);
    if (includeCursor) {
      drawRoundCursor();
      drawPencilCursor();
      drawImportedCursor();
    }
  }

  function renderLayer(layerId = activeLayerId, includeCursor = false, forceFinalPaint = false) {
    if (destroyed) return;
    flushRoundBrushStrokeSessions();
    const target = layerTargets.get(layerId);
    const paintViewMode = forceFinalPaint ? 0 : paintDisplayViewMode;
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.viewport(0, 0, canvas.width, canvas.height);
    gl.disable(gl.BLEND);
    const heightmapView = paintViewMode === 2;
    gl.clearColor(0, 0, 0, heightmapView ? 1 : 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (target && target.texture && paintViewMode === 0) drawTexture(target.texture, null, canvas.width, canvas.height, false, 1, true);
    if (oilPaintSystem) oilPaintSystem.renderLayer(layerId, null, canvas.width, canvas.height, 1, paintViewMode);
    if (includeCursor) {
      drawRoundCursor();
      drawPencilCursor();
      drawImportedCursor();
    }
  }

  function updateImportedBrushCursor(point, brush, options = {}) {
    if (destroyed || !point || !brush) {
      importedCursor = null;
      requestRender();
      return;
    }
    roundCursor = null;
    pencilCursor = null;
    importedCursor = {
      visible: true,
      point: { x: Number(point.x) || 0, y: Number(point.y) || 0 },
      brush,
      options,
      rotation: Number(brush.fixedAngle) || 0,
      seed: 1,
    };
    requestRender();
  }

  function updateRoundBrushCursor(point, size) {
    if (destroyed || !point) {
      roundCursor = null;
      requestRender();
      return;
    }
    importedCursor = null;
    pencilCursor = null;
    roundCursor = {
      visible: true,
      point: { x: Number(point.x) || 0, y: Number(point.y) || 0 },
      size: Math.max(1, Number(size) || 1),
    };
    requestRender();
  }

  function updatePencilCursor(point, options = {}) {
    if (destroyed || !point) {
      pencilCursor = null;
      requestRender();
      return;
    }
    importedCursor = null;
    roundCursor = null;
    pencilCursor = {
      visible: true,
      point: { x: Number(point.x) || 0, y: Number(point.y) || 0 },
      width: Math.max(1, Number(options.width) || 1),
      height: Math.max(1, Number(options.height) || 1),
      rotation: Number(options.rotation) || 0,
    };
    requestRender();
  }

  function hidePencilCursor() {
    if (!pencilCursor) return;
    pencilCursor = null;
    requestRender();
  }

  function hideRoundBrushCursor() {
    if (!roundCursor) return;
    roundCursor = null;
    requestRender();
  }

  function hideImportedBrushCursor() {
    if (!importedCursor) return;
    importedCursor = null;
    requestRender();
  }


  function destroyRoundBrushStrokeSession(session) {
    if (!session) return;
    if (session.baseFramebuffer) gl.deleteFramebuffer(session.baseFramebuffer);
    if (session.baseTexture) gl.deleteTexture(session.baseTexture);
    if (session.strokeFramebuffer) gl.deleteFramebuffer(session.strokeFramebuffer);
    if (session.strokeTexture) gl.deleteTexture(session.strokeTexture);
    if (session.colourFramebuffer) gl.deleteFramebuffer(session.colourFramebuffer);
    if (session.colourTexture) gl.deleteTexture(session.colourTexture);
    if (roundBrushStrokeSessions.get(session.layerId) === session) roundBrushStrokeSessions.delete(session.layerId);
  }

  function destroyAllRoundBrushStrokeSessions() {
    for (const session of Array.from(roundBrushStrokeSessions.values())) destroyRoundBrushStrokeSession(session);
    roundBrushStrokeSessions.clear();
  }

  function expandRoundBrushSessionDirtyRect(session, minimumX, minimumY, maximumX, maximumY) {
    const current = session.dirtyRect;
    session.dirtyRect = current
      ? {
          minimumX: Math.min(current.minimumX, minimumX),
          minimumY: Math.min(current.minimumY, minimumY),
          maximumX: Math.max(current.maximumX, maximumX),
          maximumY: Math.max(current.maximumY, maximumY),
        }
      : { minimumX, minimumY, maximumX, maximumY };
  }

  // The layer is defined as base + stroke while a session is open. Rebuild
  // only the region touched since the last composite instead of redrawing two
  // full-document textures for every brush segment.
  function compositeRoundBrushStrokeSession(session) {
    if (!session || !session.dirtyRect) return false;
    const target = layerTargets.get(session.layerId);
    if (!target || !target.framebuffer) return false;
    const rect = session.dirtyRect;
    session.dirtyRect = null;
    const x = Math.max(0, Math.floor(rect.minimumX) - 2);
    const top = Math.max(0, Math.floor(rect.minimumY) - 2);
    const right = Math.min(documentWidth, Math.ceil(rect.maximumX) + 2);
    const bottom = Math.min(documentHeight, Math.ceil(rect.maximumY) + 2);
    if (right <= x || bottom <= top) return false;
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(x, documentHeight - bottom, right - x, bottom - top);
    if (session.mode === 'imported') {
      compositeImportedSession(session, target.framebuffer);
    } else {
      drawTexture(session.baseTexture, target.framebuffer, documentWidth, documentHeight, false, 1, false);
      drawTexture(session.strokeTexture, target.framebuffer, documentWidth, documentHeight, false, 1, true);
    }
    gl.disable(gl.SCISSOR_TEST);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    target.containsPixels = true;
    if (session.layerId === activeLayerId) containsPixels = true;
    return true;
  }

  function flushRoundBrushStrokeSessions(layerId = null) {
    if (!roundBrushStrokeSessions.size) return;
    if (layerId != null) {
      compositeRoundBrushStrokeSession(roundBrushStrokeSessions.get(layerId));
      return;
    }
    for (const session of roundBrushStrokeSessions.values()) compositeRoundBrushStrokeSession(session);
  }

  function clearRoundBrushSessionStroke(session) {
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, session.strokeFramebuffer);
    gl.clear(gl.COLOR_BUFFER_BIT);
    if (session.colourFramebuffer) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, session.colourFramebuffer);
      gl.clear(gl.COLOR_BUFFER_BIT);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
  }

  function compositeImportedSession(session, framebuffer) {
    gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.disable(gl.BLEND);
    gl.useProgram(importedCompositeProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    const position = attribLocation(importedCompositeProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, session.baseTexture);
    gl.uniform1i(uniformLocation(importedCompositeProgram, 'u_base'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, session.strokeTexture);
    gl.uniform1i(uniformLocation(importedCompositeProgram, 'u_coverage'), 1);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, session.colourTexture || session.strokeTexture);
    gl.uniform1i(uniformLocation(importedCompositeProgram, 'u_colourTexture'), 2);
    gl.uniform1f(uniformLocation(importedCompositeProgram, 'u_useColourTexture'), session.colourTexture ? 1 : 0);
    const colour = session.colour || [0, 0, 0];
    gl.uniform3f(uniformLocation(importedCompositeProgram, 'u_colour'), colour[0], colour[1], colour[2]);
    gl.uniform1f(uniformLocation(importedCompositeProgram, 'u_strokeOpacity'), clamp(session.strokeOpacity, 0, 1));
    gl.uniform1f(uniformLocation(importedCompositeProgram, 'u_eraser'), session.isEraser ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.activeTexture(gl.TEXTURE2);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, null);
  }

  // Any other write to a layer that has an open session would be overwritten
  // by the next session composite. Fold the session into the layer first and
  // restart it from the current pixels so the external write survives.
  function prepareDirectLayerWrite(layerId = activeLayerId) {
    const session = roundBrushStrokeSessions.get(layerId);
    if (!session) return;
    compositeRoundBrushStrokeSession(session);
    const target = layerTargets.get(layerId);
    if (!target) return;
    session.rebasePending = true;
  }

  function finishDirectLayerWrite(layerId = activeLayerId) {
    const session = roundBrushStrokeSessions.get(layerId);
    if (!session || !session.rebasePending) return;
    session.rebasePending = false;
    const target = layerTargets.get(layerId);
    if (!target) return;
    drawTexture(target.texture, session.baseFramebuffer, documentWidth, documentHeight, false, 1, false);
    clearRoundBrushSessionStroke(session);
  }

  function beginRoundBrushStroke(layerId = activeLayerId) {
    if (destroyed) return false;
    setActiveLayer(layerId);
    const existing = roundBrushStrokeSessions.get(activeLayerId);
    if (existing) {
      compositeRoundBrushStrokeSession(existing);
      destroyRoundBrushStrokeSession(existing);
    }
    const baseTexture = createTexture(gl, documentWidth, documentHeight);
    const baseFramebuffer = createFramebuffer(gl, baseTexture);
    const strokeTexture = createTexture(gl, documentWidth, documentHeight);
    const strokeFramebuffer = createFramebuffer(gl, strokeTexture);
    drawTexture(documentTexture, baseFramebuffer, documentWidth, documentHeight, false, 1, false);
    const session = {
      layerId: activeLayerId,
      baseTexture,
      baseFramebuffer,
      strokeTexture,
      strokeFramebuffer,
      dirtyRect: null,
      rebasePending: false,
    };
    clearRoundBrushSessionStroke(session);
    roundBrushStrokeSessions.set(activeLayerId, session);
    return true;
  }

  function endRoundBrushStroke(layerId = activeLayerId) {
    const session = roundBrushStrokeSessions.get(String(layerId || ''));
    if (!session) return false;
    compositeRoundBrushStrokeSession(session);
    destroyRoundBrushStrokeSession(session);
    requestRender();
    return true;
  }

  function drawSegment(start, end, options = {}) {
    if (destroyed || !documentFramebuffer || !start) return;
    const safeEnd = end || start;
    const baseSize = Math.max(1, Number(options.size) || 1);
    // A real pen pressure of 0 is the lightest touch, not "unknown". Treating
    // it as 1 painted a full-size blob at the start of pen strokes.
    const startPressureValue = Number(start.pressure);
    const endPressureValue = Number(safeEnd.pressure);
    const pressureStart = Number.isFinite(startPressureValue) ? clamp(startPressureValue, 0.05, 1) : 1;
    const pressureEnd = Number.isFinite(endPressureValue) ? clamp(endPressureValue, 0.05, 1) : pressureStart;
    const radiusStart = Math.max(0.5, baseSize * pressureStart * 0.5);
    const radiusEnd = Math.max(0.5, baseSize * pressureEnd * 0.5);
    const ellipse = options.ellipse && typeof options.ellipse === 'object' ? options.ellipse : null;
    const ellipseRadiusX = ellipse ? Math.max(0.5, Number(ellipse.radiusX) || radiusStart) : 0;
    const ellipseRadiusY = ellipse ? Math.max(0.5, Number(ellipse.radiusY) || radiusStart) : 0;
    const ellipseRotation = ellipse ? Number(ellipse.angle) || 0 : 0;
    const tiltStart = ellipse
      ? { radiusX: ellipseRadiusX * pressureStart, radiusY: ellipseRadiusY * pressureStart, rotation: ellipseRotation }
      : getPenTiltTransform(start, radiusStart);
    const tiltEnd = ellipse
      ? { radiusX: ellipseRadiusX * pressureEnd, radiusY: ellipseRadiusY * pressureEnd, rotation: ellipseRotation }
      : getPenTiltTransform(safeEnd, radiusEnd);
    tiltEnd.rotation = unwrapPenRotation(tiltStart.rotation, tiltEnd.rotation);
    const padding = Math.max(
      tiltStart.radiusX,
      tiltStart.radiusY,
      tiltEnd.radiusX,
      tiltEnd.radiusY,
    ) + 2;
    const minimumX = Math.max(0, Math.min(start.x, safeEnd.x) - padding);
    const minimumY = Math.max(0, Math.min(start.y, safeEnd.y) - padding);
    const maximumX = Math.min(documentWidth, Math.max(start.x, safeEnd.x) + padding);
    const maximumY = Math.min(documentHeight, Math.max(start.y, safeEnd.y) + padding);
    if (maximumX <= minimumX || maximumY <= minimumY) return;

    const vertices = new Float32Array([
      minimumX, minimumY,
      maximumX, minimumY,
      minimumX, maximumY,
      minimumX, maximumY,
      maximumX, minimumY,
      maximumX, maximumY,
    ]);

    const strokeSession = options.nonAccumulatingOpacity ? roundBrushStrokeSessions.get(activeLayerId) || null : null;
    const useStrokeSession = Boolean(strokeSession);
    if (!useStrokeSession) prepareDirectLayerWrite(activeLayerId);
    gl.bindFramebuffer(gl.FRAMEBUFFER, useStrokeSession ? strokeSession.strokeFramebuffer : documentFramebuffer);
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.enable(gl.SCISSOR_TEST);
    gl.scissor(
      Math.floor(minimumX),
      Math.max(0, Math.floor(documentHeight - maximumY)),
      Math.max(1, Math.ceil(maximumX - minimumX)),
      Math.max(1, Math.ceil(maximumY - minimumY)),
    );
    gl.enable(gl.BLEND);
    if (useStrokeSession && blendMinMaxExtension) {
      gl.blendEquation(blendMinMaxExtension.MAX_EXT);
      gl.blendFunc(gl.ONE, gl.ONE);
    } else if (options.isEraser) {
      gl.blendEquation(gl.FUNC_ADD);
      gl.blendFuncSeparate(gl.ZERO, gl.ONE_MINUS_SRC_ALPHA, gl.ZERO, gl.ONE_MINUS_SRC_ALPHA);
    } else {
      gl.blendEquation(gl.FUNC_ADD);
      gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    }

    gl.useProgram(brushProgram);
    applySelectionUniforms(brushProgram);
    const contact = applySurfaceContact(brushProgram, 'pen', options);
    gl.uniform1f(uniformLocation(brushProgram, 'u_pressureStart'), pressureStart);
    gl.uniform1f(uniformLocation(brushProgram, 'u_pressureEnd'), pressureEnd);
    gl.bindBuffer(gl.ARRAY_BUFFER, brushBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices, gl.STREAM_DRAW);
    const position = attribLocation(brushProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);

    const colour = hexToColor(options.color);
    gl.uniform2f(uniformLocation(brushProgram, 'u_resolution'), documentWidth, documentHeight);
    gl.uniform2f(uniformLocation(brushProgram, 'u_start'), start.x, start.y);
    gl.uniform2f(uniformLocation(brushProgram, 'u_end'), safeEnd.x, safeEnd.y);
    gl.uniform2f(uniformLocation(brushProgram, 'u_radiiStart'), tiltStart.radiusX, tiltStart.radiusY);
    gl.uniform2f(uniformLocation(brushProgram, 'u_radiiEnd'), tiltEnd.radiusX, tiltEnd.radiusY);
    gl.uniform1f(uniformLocation(brushProgram, 'u_rotationStart'), tiltStart.rotation);
    gl.uniform1f(uniformLocation(brushProgram, 'u_rotationEnd'), tiltEnd.rotation);
    // Eraser types lift with their own strength and edge; markers bleed by paper absorbency.
    let softness = clamp(options.softness, 0, 100) / 100;
    if (contact.softness === 0) softness = 0;
    if (contact.bleed) softness = clamp(softness + (contact.bleed / 100) * Math.min(1.5, contact.absorbency), 0, 1);
    gl.uniform1f(uniformLocation(brushProgram, 'u_softness'), softness);
    gl.uniform1f(uniformLocation(brushProgram, 'u_opacity'), clamp((options.opacity || 1) * (contact.opacity ?? 1), 0.01, 1));
    gl.uniform3f(uniformLocation(brushProgram, 'u_colour'), colour[0], colour[1], colour[2]);
    gl.drawArrays(gl.TRIANGLES, 0, 6);

    gl.disableVertexAttribArray(position);
    gl.blendEquation(gl.FUNC_ADD);
    gl.disable(gl.BLEND);
    gl.disable(gl.SCISSOR_TEST);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (useStrokeSession) {
      expandRoundBrushSessionDirtyRect(strokeSession, minimumX, minimumY, maximumX, maximumY);
      commitActivePixels(true);
    } else {
      finishDirectLayerWrite(activeLayerId);
      commitActivePixels(options.isEraser ? containsPixels : true);
    }
    if (!options.deferRender) requestRender();
  }

  function beginPaintStroke(layerId = activeLayerId, strokeId = '', settings = {}, options = {}) {
    if (!oilPaintSystem) return false;
    return oilPaintSystem.beginStroke(layerId, strokeId, settings, options);
  }

  function endPaintStroke(strokeId = '') {
    return oilPaintSystem ? oilPaintSystem.endStroke(strokeId) : false;
  }

  // Physical paint only: resolves once the GPU has finished the paint work
  // queued so far (null when reading back now would not wait for it).
  function whenPaintIdle() {
    return oilPaintSystem && typeof oilPaintSystem.whenIdle === 'function' ? oilPaintSystem.whenIdle() : null;
  }

  function getPaintDebugMetrics(layerId = activeLayerId) {
    return oilPaintSystem ? oilPaintSystem.getDebugMetrics(layerId) : { pushReach: 0, forwardReach: 0, sideReach: 0, peakHeight: 0 };
  }

  function setPaintDisplaySettings(settings = {}) {
    const rawView = String(settings && settings.view != null ? settings.view : '').trim().toLowerCase();
    if (rawView) {
      paintDisplayViewMode = rawView === 'height' || rawView === 'heightmap'
        ? 2
        : rawView === 'color' || rawView === 'colour'
          ? 1
          : 0;
    }
    if (oilPaintSystem) oilPaintSystem.setDisplaySettings(settings);
    render(false, paintDisplayViewMode);
  }

  function dryPaintLayer(layerId = activeLayerId) {
    const result = oilPaintSystem ? oilPaintSystem.dryLayer(layerId) : false;
    if (result) requestRender();
    return result;
  }

  function clearPaintLayer(layerId = activeLayerId) {
    const result = oilPaintSystem ? oilPaintSystem.clearLayer(layerId) : false;
    if (result) requestRender();
    return result;
  }

  function exportPaintLayerState(layerId = activeLayerId) {
    if (!oilPaintSystem) return null;
    const state = oilPaintSystem.exportLayerState(layerId, canvas);
    render(true);
    return state;
  }

  function importPaintLayerState(layerId, state) {
    if (!oilPaintSystem) return Promise.resolve(false);
    return oilPaintSystem.importLayerState(layerId, state).then((result) => {
      if (result) requestRender();
      return result;
    });
  }

  // Document paper (physical paint only). It is saved with the document on its
  // own, so a paper chosen before anything is painted survives a reload too.
  function getPaperSurface() {
    return oilPaintSystem && typeof oilPaintSystem.getSurfaceSettings === 'function'
      ? oilPaintSystem.getSurfaceSettings()
      : null;
  }

  function setPaperSurface(surface) {
    if (!surface || typeof surface !== 'object' || !oilPaintSystem || typeof oilPaintSystem.setSurfaceSettings !== 'function') return false;
    oilPaintSystem.setSurfaceSettings(surface);
    requestRender();
    return true;
  }

  function drawPaintStroke(points, settings = {}, options = {}) {
    if (destroyed || !oilPaintSystem || !Array.isArray(points) || !points.length) return false;
    oilPaintSystem.setDisplaySettings(settings);
    const drew = oilPaintSystem.drawStroke(activeLayerId, points, settings, options);
    if (drew) requestRender();
    return drew;
  }

  function drawEllipseBrushStroke(points, options = {}, ellipseOptions = {}) {
    if (destroyed || !Array.isArray(points) || !points.length) return false;
    const ellipse = {
      radiusX: Math.max(0.5, Number(ellipseOptions.radiusX) || 0.5),
      radiusY: Math.max(0.5, Number(ellipseOptions.radiusY) || 0.5),
      angle: Number(ellipseOptions.angle) || 0,
    };
    if (points.length === 1) {
      drawSegment(points[0], points[0], { ...options, ellipse });
      return true;
    }
    for (let index = 1; index < points.length; index += 1) {
      drawSegment(points[index - 1], points[index], {
        ...options,
        ellipse,
        deferRender: true,
      });
    }
    requestRender();
    return true;
  }

  function drawRoundBrushStroke(points, options = {}) {
    if (destroyed || !Array.isArray(points) || !points.length) return false;
    if (points.length === 1) {
      drawSegment(points[0], points[0], options);
      return true;
    }

    for (let index = 1; index < points.length; index += 1) {
      drawSegment(points[index - 1], points[index], {
        ...options,
        deferRender: true,
      });
    }
    requestRender();
    return true;
  }

  function drawJitterSegment(start, end, options = {}, dabIndexStart = 0, includeStart = false) {
    if (destroyed || !start) return Math.max(0, Number(dabIndexStart) || 0);

    const safeEnd = end || start;
    const safeSize = Math.max(1, Number(options.size) || 1);
    const settings = options.brushSettings || createJitterBrushSettings(safeSize, options.jitterAmount);
    const spacing = getJitterBrushSpacing(safeSize, settings);
    const blobScale = getJitterBlobScale(settings);
    const strokeSeed = Number.isFinite(Number(options.strokeSeed)) ? Number(options.strokeSeed) : 1;
    const dx = safeEnd.x - start.x;
    const dy = safeEnd.y - start.y;
    const distance = Math.hypot(dx, dy);
    const steps = end ? Math.max(1, Math.ceil(distance / spacing)) : 0;
    const firstStep = includeStart || !end ? 0 : 1;
    let dabIndex = Math.max(0, Number(dabIndexStart) || 0);

    for (let step = firstStep; step <= steps; step += 1) {
      const t = steps > 0 ? step / steps : 0;
      const startPressure = Number.isFinite(Number(start.pressure)) ? Number(start.pressure) : 1;
      const endPressure = Number.isFinite(Number(safeEnd.pressure)) ? Number(safeEnd.pressure) : startPressure;
      const pressure = startPressure + ((endPressure - startPressure) * t);
      const radius = Math.max(0.5, safeSize * Math.max(0.05, pressure) * 0.5);
      const pointX = start.x + (dx * t);
      const pointY = start.y + (dy * t);
      const baseSeed = combineSeed(strokeSeed, dabIndex, pointX * 100, pointY * 100);

      for (let layerIndex = 0; layerIndex < 3; layerIndex += 1) {
        const profile = buildJitterBlobProfile(
          combineSeed(baseSeed, layerIndex * 911),
          dabIndex + layerIndex,
          radius,
          settings,
        );
        const layerRadius = radius * (0.82 + layerIndex * 0.06) * blobScale;
        const alphaScale = layerIndex === 0 ? 0.58 : layerIndex === 1 ? 0.34 : 0.22;
        const center = {
          x: pointX + profile.offsetX,
          y: pointY + profile.offsetY,
          pressure: 1,
        };

        drawSegment(center, center, {
          ...options,
          opacity: clamp((Number(options.opacity) || 1) * alphaScale, 0.01, 1),
          size: layerRadius * 2,
          ellipse: {
            radiusX: Math.max(0.5, layerRadius * profile.stretchX),
            radiusY: Math.max(0.5, layerRadius * profile.stretchY),
            angle: profile.rotation,
          },
          deferRender: true,
        });

        if (!options.isEraser && Number(options.softness) > 0) {
          const bloomRadius = layerRadius * 0.94;
          drawSegment(center, center, {
            ...options,
            opacity: clamp((Number(options.opacity) || 1) * alphaScale * 0.12, 0.01, 1),
            size: bloomRadius * 2,
            softness: Math.max(Number(options.softness) || 0, 35),
            ellipse: {
              radiusX: Math.max(0.5, bloomRadius * (profile.stretchX + 0.08)),
              radiusY: Math.max(0.5, bloomRadius * (profile.stretchY + 0.08)),
              angle: profile.rotation,
            },
            deferRender: true,
          });
        }
      }

      dabIndex += 1;
    }

    requestRender();
    return dabIndex;
  }

  function createImportedTipTexture(brush, dab, options = {}) {
    if (!brush || !dab) return null;

    const source = getImportedBrushGpuTipCanvas(
      brush,
      dab.seed || dab.tipIndex || 0,
      clamp(options.softness, 0, 100),
      !!options.isEraser,
    );
    if (!source) return null;

    const cached = importedTipTextureCache.get(source);
    if (cached) return cached;

    const texture = gl.createTexture();
    if (!texture) return null;

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.bindTexture(gl.TEXTURE_2D, null);

    const record = {
      texture,
      width: Math.max(1, Number(source.width) || 1),
      height: Math.max(1, Number(source.height) || 1),
    };
    importedTipTextureCache.set(source, record);
    importedTipTextures.add(texture);
    return record;
  }

  function drawImportedBrushDabs(dabs, brush, options = {}) {
    if (destroyed || !documentFramebuffer || !brush || !Array.isArray(dabs) || !dabs.length) return false;
    prepareDirectLayerWrite(activeLayerId);
    const colour = hexToColor(options.color || '#111111');
    gl.bindFramebuffer(gl.FRAMEBUFFER, documentFramebuffer);
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.enable(gl.BLEND);
    if (options.isEraser) {
      gl.blendFuncSeparate(gl.ZERO, gl.ONE_MINUS_SRC_ALPHA, gl.ZERO, gl.ONE_MINUS_SRC_ALPHA);
    } else {
      gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    }
    gl.useProgram(importedProgram);
    applySelectionUniforms(importedProgram);
    applySurfaceContact(importedProgram, 'imported', options);
    gl.uniform1f(uniformLocation(importedProgram, 'u_contactPressure'), 0.7);
    gl.uniform2f(uniformLocation(importedProgram, 'u_resolution'), documentWidth, documentHeight);
    gl.uniform3f(uniformLocation(importedProgram, 'u_colour'), colour[0], colour[1], colour[2]);
    gl.bindBuffer(gl.ARRAY_BUFFER, importedBuffer);
    const pos = attribLocation(importedProgram, 'a_position');
    const uv = attribLocation(importedProgram, 'a_uv');
    gl.enableVertexAttribArray(pos);
    gl.enableVertexAttribArray(uv);
    gl.vertexAttribPointer(pos, 2, gl.FLOAT, false, 16, 0);
    gl.vertexAttribPointer(uv, 2, gl.FLOAT, false, 16, 8);

    for (const dab of dabs) {
      const tip = createImportedTipTexture(brush, dab, options);
      if (!tip) continue;
      const size = Math.max(0.5, (Number(options.size) || 1) * Math.max(0.02, Number(dab.sizeMultiplier) || 1) * Math.max(0.05, Number(brush.tipScale) || 1));
      const sourceMaximum = Math.max(tip.width, tip.height);
      const halfWidth = (size * (tip.width / sourceMaximum)) * 0.5;
      const halfHeight = (size * (tip.height / sourceMaximum)) * 0.5;
      const angle = Number(dab.rotation) || 0;
      const c = Math.cos(angle), si = Math.sin(angle);
      const corners = [[-halfWidth,-halfHeight,0,0],[halfWidth,-halfHeight,1,0],[-halfWidth,halfHeight,0,1],[-halfWidth,halfHeight,0,1],[halfWidth,-halfHeight,1,0],[halfWidth,halfHeight,1,1]];
      const vertices=[];
      for (const [x,y,u,v] of corners) vertices.push(Number(dab.x)+x*c-y*si, Number(dab.y)+x*si+y*c, u, v);
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STREAM_DRAW);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tip.texture);
      gl.uniform1i(uniformLocation(importedProgram, 'u_tip'), 0);
      gl.uniform1f(uniformLocation(importedProgram, 'u_opacity'), clamp(Number(dab.opacity) || 0, 0.001, 1));
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }
    gl.disableVertexAttribArray(pos);
    gl.disableVertexAttribArray(uv);
    gl.disable(gl.BLEND);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    finishDirectLayerWrite(activeLayerId);
    commitActivePixels(true);
    requestRender();
    return true;
  }

  function drawPaintImportedBrushDabs(dabs, brush, settings = {}, options = {}) {
    if (destroyed || !oilPaintSystem || !brush || !Array.isArray(dabs) || !dabs.length) return false;
    oilPaintSystem.setDisplaySettings(settings);
    let drew = false;

    for (const dab of dabs) {
      const tip = createImportedTipTexture(brush, dab, options);
      if (!tip) continue;
      if (oilPaintSystem.drawImportedDab(activeLayerId, dab, tip, brush, settings, options)) drew = true;
    }

    if (drew) requestRender();
    return drew;
  }


  function createCanvasTexture(source, repeat) {
    const cached = importedTipTextureCache.get(source);
    if (cached) return cached;
    const texture = gl.createTexture();
    if (!texture) return null;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, repeat ? gl.REPEAT : gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, repeat ? gl.REPEAT : gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.bindTexture(gl.TEXTURE_2D, null);
    const record = { texture, width: Math.max(1, Number(source.width) || 1), height: Math.max(1, Number(source.height) || 1) };
    importedTipTextureCache.set(source, record);
    importedTipTextures.add(texture);
    return record;
  }

  function selectImportedTip(brush, dab) {
    const tips = Array.isArray(brush.originalTipCanvases) && brush.originalTipCanvases.length ? brush.originalTipCanvases : [brush.originalTipCanvas];
    const count = tips.length;
    if (count <= 1) return tips[0] || null;
    const mode = brush.tipSelection || 'random';
    let index;
    if (mode === 'sequential') index = Math.abs(Math.floor(Number(dab.tipIndex) || 0));
    else if (mode === 'pressure') index = Math.round(clamp(Number(dab.pressure) || 1, 0, 1) * (count - 1));
    else if (mode === 'angle') index = Math.floor((((Number(dab.rotation) || 0) % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2) / (Math.PI * 2) * count);
    else index = Math.abs(Math.floor(Math.sin((Number(dab.seed) || 1) * 12.9898) * 43758.5453));
    return tips[index % count] || tips[0];
  }

  // Opens a stroke session for a schema-v2 imported brush. The layer is
  // rebuilt as base + stroke while the session is open (see
  // compositeImportedSession), so opacity caps and end-taper re-renders
  // never touch pixels outside the stroke.
  function beginImportedStroke(layerId = activeLayerId, options = {}) {
    if (destroyed) return false;
    if (!beginRoundBrushStroke(layerId)) return false;
    const session = roundBrushStrokeSessions.get(activeLayerId);
    if (!session) return false;
    session.mode = 'imported';
    session.strokeOpacity = clamp(Number(options.strokeOpacity == null ? 1 : options.strokeOpacity), 0, 1);
    session.colour = hexToColor(options.color || '#111111');
    session.isEraser = !!options.isEraser;
    session.strokeRect = null;
    if (options.useColourTexture) {
      session.colourTexture = createTexture(gl, documentWidth, documentHeight);
      session.colourFramebuffer = createFramebuffer(gl, session.colourTexture);
      clearRoundBrushSessionStroke(session);
    }
    return true;
  }

  function writeImportedDabs(session, dabs, brush, options) {
    const texture = brush.brushTexture || null;
    const grainCanvas = texture ? brush.originalGrainCanvas : null;
    const grain = grainCanvas ? createCanvasTexture(grainCanvas, true) : null;
    const runs = [];
    let minimumX = Infinity;
    let minimumY = Infinity;
    let maximumX = -Infinity;
    let maximumY = -Infinity;
    const baseColour = session.colour || [0, 0, 0];

    for (const dab of dabs) {
      const tipCanvas = selectImportedTip(brush, dab);
      if (!tipCanvas) continue;
      const tip = createCanvasTexture(tipCanvas, false);
      if (!tip) continue;
      const size = Math.max(0.25, Number(dab.size) || 1);
      const longest = Math.max(tip.width, tip.height);
      const halfWidth = size * (tip.width / longest) * 0.5 * (Number(dab.scaleX) || 1);
      const halfHeight = size * (tip.height / longest) * 0.5 * (Number(dab.scaleY) || 1);
      const angle = Number(dab.rotation) || 0;
      const c = Math.cos(angle);
      const s = Math.sin(angle);
      const colour = Array.isArray(dab.colour) ? dab.colour : baseColour;
      let run = runs[runs.length - 1];
      if (!run || run.tip !== tip) {
        run = { tip, values: [] };
        runs.push(run);
      }
      const corners = [[-halfWidth, -halfHeight, 0, 1], [halfWidth, -halfHeight, 1, 1], [-halfWidth, halfHeight, 0, 0], [-halfWidth, halfHeight, 0, 0], [halfWidth, -halfHeight, 1, 1], [halfWidth, halfHeight, 1, 0]];
      for (const [x, y, u, v] of corners) {
        const px = Number(dab.x) + x * c - y * s;
        const py = Number(dab.y) + x * s + y * c;
        if (px < minimumX) minimumX = px;
        if (py < minimumY) minimumY = py;
        if (px > maximumX) maximumX = px;
        if (py > maximumY) maximumY = py;
        run.values.push(px, py, u, v, clamp(Number(dab.flow) || 0, 0, 1), clamp(Number(dab.opacity == null ? 1 : dab.opacity), 0, 1), Number(dab.grainX) || 0, Number(dab.grainY) || 0, colour[0], colour[1], colour[2]);
      }
    }
    if (!runs.length) return null;

    gl.useProgram(importedDabProgram);
    applySelectionUniforms(importedDabProgram);
    applySurfaceContact(importedDabProgram, 'imported', options);
    gl.uniform1f(uniformLocation(importedDabProgram, 'u_contactPressure'), 0.7);
    gl.uniform2f(uniformLocation(importedDabProgram, 'u_resolution'), documentWidth, documentHeight);
    gl.uniform1f(uniformLocation(importedDabProgram, 'u_tipColour'), brush.tipColour === 'image' ? 1 : 0);
    gl.uniform1f(uniformLocation(importedDabProgram, 'u_grainEnabled'), grain ? 1 : 0);
    if (grain && texture) {
      gl.activeTexture(gl.TEXTURE1);
      gl.bindTexture(gl.TEXTURE_2D, grain.texture);
      gl.uniform1i(uniformLocation(importedDabProgram, 'u_grain'), 1);
      gl.activeTexture(gl.TEXTURE0);
      const sourceSize = Number(grainCanvas.grainSourceSize) || grain.width;
      const relative = texture.mode === 'moving' ? Math.max(8, Number(options.size) || 32) / 64 : 1;
      gl.uniform1f(uniformLocation(importedDabProgram, 'u_grainScale'), Math.max(4, sourceSize * clamp(Number(texture.scale) || 1, 0.01, 16) * relative));
      gl.uniform1f(uniformLocation(importedDabProgram, 'u_grainDepth'), clamp(Number(texture.depth == null ? 1 : texture.depth), 0, 1));
      gl.uniform1f(uniformLocation(importedDabProgram, 'u_grainDepthMinimum'), clamp(Number(texture.depthMinimum) || 0, 0, 1));
      gl.uniform1f(uniformLocation(importedDabProgram, 'u_grainInvert'), texture.invert ? 1 : 0);
      gl.uniform1f(uniformLocation(importedDabProgram, 'u_grainBrightness'), Number(texture.brightness) || 0);
      gl.uniform1f(uniformLocation(importedDabProgram, 'u_grainContrast'), Number(texture.contrast) || 0);
      gl.uniform1f(uniformLocation(importedDabProgram, 'u_grainRotation'), Number(texture.rotation) || 0);
      gl.uniform1f(uniformLocation(importedDabProgram, 'u_grainSubtract'), texture.blend === 'subtract' ? 1 : 0);
    }
    gl.bindBuffer(gl.ARRAY_BUFFER, importedDabBuffer);
    const stride = 11 * 4;
    const attributes = [['a_position', 2, 0], ['a_uv', 2, 8], ['a_params', 4, 16], ['a_colour', 3, 32]].map(([name, size, offset]) => {
      const location = attribLocation(importedDabProgram, name);
      gl.enableVertexAttribArray(location);
      return { location, size, offset };
    });
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.enable(gl.BLEND);
    gl.blendEquation(gl.FUNC_ADD);
    gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    const passes = session.colourFramebuffer ? [[session.strokeFramebuffer, 0], [session.colourFramebuffer, 1]] : [[session.strokeFramebuffer, 0]];
    for (const run of runs) {
      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(run.values), gl.STREAM_DRAW);
      attributes.forEach(({ location, size, offset }) => gl.vertexAttribPointer(location, size, gl.FLOAT, false, stride, offset));
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, run.tip.texture);
      gl.uniform1i(uniformLocation(importedDabProgram, 'u_tip'), 0);
      for (const [framebuffer, colourPass] of passes) {
        gl.bindFramebuffer(gl.FRAMEBUFFER, framebuffer);
        gl.uniform1f(uniformLocation(importedDabProgram, 'u_colourPass'), colourPass);
        gl.drawArrays(gl.TRIANGLES, 0, run.values.length / 11);
      }
    }
    attributes.forEach(({ location }) => gl.disableVertexAttribArray(location));
    gl.disable(gl.BLEND);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { minimumX, minimumY, maximumX, maximumY };
  }

  function drawImportedBrushDabsV2(dabs, brush, options = {}) {
    if (destroyed || !brush || !Array.isArray(dabs) || !dabs.length) return false;
    let session = roundBrushStrokeSessions.get(activeLayerId);
    if (!session || session.mode !== 'imported') {
      beginImportedStroke(activeLayerId, options);
      session = roundBrushStrokeSessions.get(activeLayerId);
    }
    if (!session) return false;
    const rect = writeImportedDabs(session, dabs, brush, options);
    if (!rect) return false;
    expandRoundBrushSessionDirtyRect(session, rect.minimumX - 2, rect.minimumY - 2, rect.maximumX + 2, rect.maximumY + 2);
    const total = session.strokeRect;
    session.strokeRect = total
      ? { minimumX: Math.min(total.minimumX, rect.minimumX), minimumY: Math.min(total.minimumY, rect.minimumY), maximumX: Math.max(total.maximumX, rect.maximumX), maximumY: Math.max(total.maximumY, rect.maximumY) }
      : rect;
    compositeRoundBrushStrokeSession(session);
    commitActivePixels(true);
    requestRender();
    return true;
  }

  // Clears the open stroke and paints `dabs` again, used to apply an end
  // taper once the stroke length is known.
  function redrawImportedStroke(dabs, brush, options = {}) {
    const session = roundBrushStrokeSessions.get(activeLayerId);
    if (!session || session.mode !== 'imported' || !session.strokeRect) return false;
    const previous = session.strokeRect;
    clearRoundBrushSessionStroke(session);
    session.strokeRect = null;
    expandRoundBrushSessionDirtyRect(session, previous.minimumX - 2, previous.minimumY - 2, previous.maximumX + 2, previous.maximumY + 2);
    const rect = writeImportedDabs(session, dabs, brush, options);
    if (rect) expandRoundBrushSessionDirtyRect(session, rect.minimumX - 2, rect.minimumY - 2, rect.maximumX + 2, rect.maximumY + 2);
    session.strokeRect = rect || previous;
    compositeRoundBrushStrokeSession(session);
    requestRender();
    return true;
  }

  function createPencilTipTexture(stamp) {
    if (!stamp) return null;
    const source = getPencilBrushTipCanvas(
      Math.max(1, Math.ceil(Number(stamp.size) || 1)),
      Number(stamp.stampSeed) || 0,
    );
    if (!source) return null;

    const cached = importedTipTextureCache.get(source);
    if (cached) return cached;

    const texture = gl.createTexture();
    if (!texture) return null;

    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, source);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.bindTexture(gl.TEXTURE_2D, null);

    const record = {
      texture,
      width: Math.max(1, Number(source.width) || 1),
      height: Math.max(1, Number(source.height) || 1),
    };
    importedTipTextureCache.set(source, record);
    importedTipTextures.add(texture);
    return record;
  }

  // Mechanical, coloured, charcoal, pastel, chalk and crayon: material
  // deposition on the stamp path (one batched draw). Graphite never comes here.
  function drawDryMediaDabs(stamps, options, medium) {
    const strokeKey = `${options.strokeSeed || 0}|${activeLayerId}|${medium}`;
    if (!dryMediaStroke || dryMediaStroke.key !== strokeKey) dryMediaStroke = { key: strokeKey, seed: Number(options.strokeSeed) || 0 };
    const orientation = options.pencilOrientation && typeof options.pencilOrientation === 'object' ? options.pencilOrientation : null;
    const elevation = clamp(Number(orientation && orientation.elevation) || 90, 15, 90);
    const grade = (Number(options.pencilGrade) || 0) / 8;
    const built = buildDryMediaVertices(stamps, {
      medium,
      size: options.size,
      opacity: options.opacity,
      tilt: (90 - elevation) / 75,
    }, dryMediaStroke);
    if (!built.count) return false;
    prepareDirectLayerWrite(activeLayerId);
    gl.bindFramebuffer(gl.FRAMEBUFFER, documentFramebuffer);
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(dryMedia.program);
    applySelectionUniforms(dryMedia.program);
    applySurfaceContact(dryMedia.program, 'pencil', options);
    dryMedia.draw(built.vertices, built.count, {
      colour: hexToColor(options.color || '#111111'),
      medium,
      density: mediumDensity(medium, grade),
      width: documentWidth,
      height: documentHeight,
    });
    gl.disable(gl.BLEND);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    finishDirectLayerWrite(activeLayerId);
    commitActivePixels(true);
    requestRender();
    return true;
  }

  function drawPencilDabs(stamps, options = {}) {
    if (destroyed || !documentFramebuffer || !Array.isArray(stamps) || !stamps.length) return false;
    const pencilMedium = (options.surfaceMedia || getSurfaceMedia()).pencil;
    if (dryMedia && !options.isEraser && isDryMedium(pencilMedium)) return drawDryMediaDabs(stamps, options, pencilMedium);
    prepareDirectLayerWrite(activeLayerId);

    const colour = hexToColor(options.color || '#111111');
    gl.bindFramebuffer(gl.FRAMEBUFFER, documentFramebuffer);
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(importedProgram);
    applySelectionUniforms(importedProgram);
    applySurfaceContact(importedProgram, 'pencil', options);
    gl.uniform2f(uniformLocation(importedProgram, 'u_resolution'), documentWidth, documentHeight);
    gl.uniform3f(uniformLocation(importedProgram, 'u_colour'), colour[0], colour[1], colour[2]);
    gl.bindBuffer(gl.ARRAY_BUFFER, importedBuffer);

    const position = attribLocation(importedProgram, 'a_position');
    const uv = attribLocation(importedProgram, 'a_uv');
    gl.enableVertexAttribArray(position);
    gl.enableVertexAttribArray(uv);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 16, 0);
    gl.vertexAttribPointer(uv, 2, gl.FLOAT, false, 16, 8);

    for (const stamp of stamps) {
      const tip = createPencilTipTexture(stamp);
      if (!tip) continue;

      const size = Math.max(0.5, Number(stamp.size) || 1);
      const halfWidth = size * Math.max(0.05, Number(stamp.scaleX) || 1) * 0.5;
      const halfHeight = size * Math.max(0.05, Number(stamp.scaleY) || 1) * 0.5;
      const angle = Number(stamp.rotation) || 0;
      const cosine = Math.cos(angle);
      const sine = Math.sin(angle);
      const x = Number(stamp.point && stamp.point.x) || 0;
      const y = Number(stamp.point && stamp.point.y) || 0;
      const corners = [
        [-halfWidth, -halfHeight, 0, 0],
        [halfWidth, -halfHeight, 1, 0],
        [-halfWidth, halfHeight, 0, 1],
        [-halfWidth, halfHeight, 0, 1],
        [halfWidth, -halfHeight, 1, 0],
        [halfWidth, halfHeight, 1, 1],
      ];
      const vertices = [];

      for (const [localX, localY, u, v] of corners) {
        vertices.push(
          x + localX * cosine - localY * sine,
          y + localX * sine + localY * cosine,
          u,
          v,
        );
      }

      gl.bufferData(gl.ARRAY_BUFFER, new Float32Array(vertices), gl.STREAM_DRAW);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, tip.texture);
      gl.uniform1i(uniformLocation(importedProgram, 'u_tip'), 0);
      gl.uniform1f(uniformLocation(importedProgram, 'u_opacity'), clamp(Number(stamp.alpha) || 0, 0.001, 1));
      const stampPressure = Number(stamp.point && (stamp.point.inputPressure ?? stamp.point.pressure));
      gl.uniform1f(uniformLocation(importedProgram, 'u_contactPressure'), Number.isFinite(stampPressure) && stampPressure > 0 ? clamp(stampPressure, 0, 1) : 0.6);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
    }

    gl.disableVertexAttribArray(position);
    gl.disableVertexAttribArray(uv);
    gl.disable(gl.BLEND);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    finishDirectLayerWrite(activeLayerId);
    commitActivePixels(true);
    requestRender();
    return true;
  }


  function drawAirbrushDabs(dabs, options = {}) {
    if (destroyed || !documentFramebuffer || !Array.isArray(dabs) || !dabs.length) return false;

    const floatsPerVertex = 6;
    const vertices = new Float32Array(dabs.length * 6 * floatsPerVertex);
    let offset = 0;

    for (const dab of dabs) {
      const x = Number(dab.x) || 0;
      const y = Number(dab.y) || 0;
      const radius = Math.max(0.25, (Number(dab.size) || 1) * 0.5);
      const softness = clamp(dab.softness, 0, 1);
      const opacity = clamp(dab.opacity, 0, 1);
      if (opacity <= 0.001) continue;

      const corners = [
        [-radius, -radius, -1, -1],
        [radius, -radius, 1, -1],
        [-radius, radius, -1, 1],
        [-radius, radius, -1, 1],
        [radius, -radius, 1, -1],
        [radius, radius, 1, 1],
      ];

      for (const corner of corners) {
        vertices[offset] = x + corner[0];
        vertices[offset + 1] = y + corner[1];
        vertices[offset + 2] = corner[2];
        vertices[offset + 3] = corner[3];
        vertices[offset + 4] = softness;
        vertices[offset + 5] = opacity;
        offset += floatsPerVertex;
      }
    }

    if (!offset) return false;

    prepareDirectLayerWrite(activeLayerId);
    const colour = hexToColor(options.color || '#111111');
    gl.bindFramebuffer(gl.FRAMEBUFFER, documentFramebuffer);
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(airbrushProgram);
    applySelectionUniforms(airbrushProgram);
    applySurfaceContact(airbrushProgram, 'airbrush', options);
    gl.bindBuffer(gl.ARRAY_BUFFER, airbrushBuffer);
    gl.bufferData(gl.ARRAY_BUFFER, vertices.subarray(0, offset), gl.STREAM_DRAW);

    const stride = floatsPerVertex * 4;
    const position = attribLocation(airbrushProgram, 'a_position');
    const local = attribLocation(airbrushProgram, 'a_local');
    const softness = attribLocation(airbrushProgram, 'a_softness');
    const opacity = attribLocation(airbrushProgram, 'a_opacity');

    gl.enableVertexAttribArray(position);
    gl.enableVertexAttribArray(local);
    gl.enableVertexAttribArray(softness);
    gl.enableVertexAttribArray(opacity);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, stride, 0);
    gl.vertexAttribPointer(local, 2, gl.FLOAT, false, stride, 8);
    gl.vertexAttribPointer(softness, 1, gl.FLOAT, false, stride, 16);
    gl.vertexAttribPointer(opacity, 1, gl.FLOAT, false, stride, 20);

    gl.uniform2f(uniformLocation(airbrushProgram, 'u_resolution'), documentWidth, documentHeight);
    gl.uniform3f(uniformLocation(airbrushProgram, 'u_colour'), colour[0], colour[1], colour[2]);
    gl.drawArrays(gl.TRIANGLES, 0, offset / floatsPerVertex);

    gl.disableVertexAttribArray(position);
    gl.disableVertexAttribArray(local);
    gl.disableVertexAttribArray(softness);
    gl.disableVertexAttribArray(opacity);
    gl.disable(gl.BLEND);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    finishDirectLayerWrite(activeLayerId);
    commitActivePixels(true);
    requestRender();
    return true;
  }

  function destroyLayerMoveSession() {
    if (!layerMoveSession) return;
    if (layerMoveSession.framebuffer) gl.deleteFramebuffer(layerMoveSession.framebuffer);
    if (layerMoveSession.texture) gl.deleteTexture(layerMoveSession.texture);
    layerMoveSession = null;
  }

  function beginLayerMove(layerId = activeLayerId) {
    const safeLayerId = String(layerId || '').trim();
    const target = layerTargets.get(safeLayerId);
    flushRoundBrushStrokeSessions(safeLayerId);
    if (!target || !target.containsPixels) return false;

    destroyLayerMoveSession();
    const texture = createTexture(gl, documentWidth, documentHeight);
    const framebuffer = createFramebuffer(gl, texture);
    drawTexture(target.texture, framebuffer, documentWidth, documentHeight, false, 1, false);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    if (oilPaintSystem) oilPaintSystem.beginMove(safeLayerId);

    layerMoveSession = {
      layerId: safeLayerId,
      texture,
      framebuffer,
      containsPixels: Boolean(target.containsPixels),
      offsetX: 0,
      offsetY: 0,
    };
    return true;
  }

  function updateLayerMove(offsetX, offsetY) {
    if (!layerMoveSession) return false;
    const target = layerTargets.get(layerMoveSession.layerId);
    if (!target) return false;

    const nextOffsetX = Math.round(Number(offsetX) || 0);
    const nextOffsetY = Math.round(Number(offsetY) || 0);

    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.disable(gl.SCISSOR_TEST);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);

    gl.viewport(nextOffsetX, -nextOffsetY, documentWidth, documentHeight);
    gl.useProgram(displayProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    const position = attribLocation(displayProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, layerMoveSession.texture);
    gl.uniform1i(uniformLocation(displayProgram, 'u_texture'), 0);
    gl.uniform1f(uniformLocation(displayProgram, 'u_forceOpaque'), 0);
    gl.uniform1f(uniformLocation(displayProgram, 'u_opacity'), 1);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    if (oilPaintSystem) oilPaintSystem.updateMove(nextOffsetX, nextOffsetY);

    layerMoveSession.offsetX = nextOffsetX;
    layerMoveSession.offsetY = nextOffsetY;
    target.containsPixels = layerMoveSession.containsPixels;
    if (layerMoveSession.layerId === activeLayerId) containsPixels = target.containsPixels;
    requestRender();
    return true;
  }

  function endLayerMove(cancel = false) {
    if (!layerMoveSession) return false;
    if (cancel) updateLayerMove(0, 0);
    if (oilPaintSystem) oilPaintSystem.endMove(cancel);
    destroyLayerMoveSession();
    requestRender();
    return true;
  }

  function clear() {
    clearLayer(activeLayerId);
  }

  function loadImage(image) {
    if (destroyed || !image || !documentFramebuffer) return;
    clear();
    const texture = gl.createTexture();
    if (!texture) return;
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);

    const scale = Math.min(documentWidth / image.width, documentHeight / image.height);
    const drawWidth = Math.max(1, Math.round(image.width * scale));
    const drawHeight = Math.max(1, Math.round(image.height * scale));
    const drawX = Math.round((documentWidth - drawWidth) / 2);
    const drawY = Math.round((documentHeight - drawHeight) / 2);

    gl.bindFramebuffer(gl.FRAMEBUFFER, documentFramebuffer);
    gl.viewport(drawX, documentHeight - drawY - drawHeight, drawWidth, drawHeight);
    gl.disable(gl.BLEND);
    gl.useProgram(displayProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    const position = attribLocation(displayProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1i(uniformLocation(displayProgram, 'u_texture'), 0);
    gl.uniform1f(uniformLocation(displayProgram, 'u_forceOpaque'), 0);
    gl.uniform1f(uniformLocation(displayProgram, 'u_opacity'), 1);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.deleteTexture(texture);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    commitActivePixels(true);
    requestRender();
  }

  function captureSnapshot() {
    if (destroyed) return null;
    flushRoundBrushStrokeSessions();
    const layers = [];
    for (const layer of layerState) {
      const target = layerTargets.get(layer.id);
      if (!target) continue;
      const texture = createTexture(gl, documentWidth, documentHeight);
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.copyTexSubImage2D(gl.TEXTURE_2D, 0, 0, 0, 0, 0, documentWidth, documentHeight);
      gl.bindTexture(gl.TEXTURE_2D, null);
      const paint = oilPaintSystem ? oilPaintSystem.captureLayerSnapshot(layer.id) : null;
      if (oilPaintSystem && oilPaintSystem.hasPaint(layer.id) && !paint) {
        gl.deleteTexture(texture);
        for (const saved of layers) {
          if (saved.texture) gl.deleteTexture(saved.texture);
          if (saved.paint) oilPaintSystem.destroyLayerSnapshot(saved.paint);
        }
        gl.bindFramebuffer(gl.FRAMEBUFFER, null);
        return null;
      }
      layers.push({
        id: layer.id,
        texture,
        paint,
        containsPixels: target.containsPixels,
      });
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { kind: 'gpu-layer-document', width: documentWidth, height: documentHeight, activeLayerId, layerState: layerState.map((layer) => ({ ...layer })), layers };
  }

  function restoreSnapshot(snapshot) {
    if (destroyed || !snapshot) return;
    if (snapshot.kind !== 'gpu-layer-document') {
      if (!snapshot.texture || !documentFramebuffer) return;
      drawTexture(snapshot.texture, documentFramebuffer, documentWidth, documentHeight, false, 1, false);
      commitActivePixels(Boolean(snapshot.containsPixels));
      requestRender();
      return;
    }
    layerState = Array.isArray(snapshot.layerState) && snapshot.layerState.length
      ? snapshot.layerState.map((layer) => ({ ...layer }))
      : [{ id: 'layer-1', name: 'Layer 1', visible: true, opacity: 1 }];
    activeLayerId = snapshot.activeLayerId && layerState.some((layer) => layer.id === snapshot.activeLayerId)
      ? snapshot.activeLayerId
      : layerState[layerState.length - 1].id;
    createDocumentTarget();
    for (const saved of snapshot.layers || []) {
      const target = layerTargets.get(saved.id);
      if (!target || !saved.texture) continue;
      drawTexture(saved.texture, target.framebuffer, documentWidth, documentHeight, false, 1, false);
      if (oilPaintSystem && saved.paint) oilPaintSystem.restoreLayerSnapshot(saved.id, saved.paint);
      target.containsPixels = Boolean(saved.containsPixels) || Boolean(oilPaintSystem && oilPaintSystem.hasPaint(saved.id));
    }
    syncActiveTarget();
    requestRender();
  }

  function restoreSnapshotLayers(snapshot, layerIds, nextLayers, nextActiveLayerId = activeLayerId) {
    if (destroyed || !snapshot || snapshot.kind !== 'gpu-layer-document') return false;
    const restoreIds = new Set(
      (Array.isArray(layerIds) ? layerIds : [])
        .map((id) => String(id || '').trim())
        .filter(Boolean)
    );
    if (!restoreIds.size) return false;

    const safeLayers = Array.isArray(nextLayers) && nextLayers.length
      ? nextLayers.map((layer, index) => ({
        ...layer,
        id: String(layer && layer.id ? layer.id : `layer-${index + 1}`),
        name: String(layer && layer.name ? layer.name : `Layer ${index + 1}`),
        visible: !layer || layer.visible !== false,
        opacity: clamp(layer && layer.opacity == null ? 1 : layer.opacity, 0, 1),
        ownerName: String(layer && layer.ownerName ? layer.ownerName : '').trim(),
        ownerClientId: String(layer && layer.ownerClientId ? layer.ownerClientId : '').trim(),
        ownerUserId: Number(layer && (layer.ownerUserId || layer.owner_user_id)) || 0,
      }))
      : layerState.map((layer) => ({ ...layer }));

    setLayers(safeLayers, nextActiveLayerId);

    for (const saved of snapshot.layers || []) {
      const savedId = String(saved && saved.id ? saved.id : '').trim();
      if (!savedId || !restoreIds.has(savedId) || !saved.texture) continue;
      const target = layerTargets.get(savedId);
      if (!target) continue;
      drawTexture(saved.texture, target.framebuffer, documentWidth, documentHeight, false, 1, false);
      if (oilPaintSystem && saved.paint) oilPaintSystem.restoreLayerSnapshot(savedId, saved.paint);
      target.containsPixels = Boolean(saved.containsPixels) || Boolean(oilPaintSystem && oilPaintSystem.hasPaint(savedId));
    }

    activeLayerId = safeLayers.some((layer) => layer.id === nextActiveLayerId)
      ? nextActiveLayerId
      : safeLayers[safeLayers.length - 1].id;
    syncActiveTarget();
    requestRender();
    return true;
  }

  function destroySnapshot(snapshot) {
    if (!snapshot || snapshot.kind === 'projection-history-only') return;
    if (!gl.isContextLost() && snapshot.texture && gl.isTexture(snapshot.texture)) gl.deleteTexture(snapshot.texture);
    if (Array.isArray(snapshot.layers)) {
      for (const layer of snapshot.layers) {
        if (!gl.isContextLost() && layer?.texture && gl.isTexture(layer.texture)) gl.deleteTexture(layer.texture);
        if (oilPaintSystem && layer?.paint) oilPaintSystem.destroyLayerSnapshot(layer.paint);
      }
    }
  }

  function setLayers(nextLayers, nextActiveLayerId = activeLayerId) {
    destroyLayerMoveSession();
    const safeLayers = Array.isArray(nextLayers) && nextLayers.length
      ? nextLayers.map((layer, index) => ({
        ...layer,
        id: String(layer.id || `layer-${index + 1}`),
        name: String(layer.name || `Layer ${index + 1}`),
        visible: layer.visible !== false,
        opacity: clamp(layer.opacity == null ? 1 : layer.opacity, 0, 1),
        ownerName: String(layer.ownerName || '').trim(),
        ownerClientId: String(layer.ownerClientId || '').trim(),
        ownerUserId: Number(layer.ownerUserId || layer.owner_user_id || 0),
      }))
      : [{ id: 'layer-1', name: 'Layer 1', visible: true, opacity: 1 }];
    const keep = new Set(safeLayers.map((layer) => layer.id));
    for (const session of Array.from(roundBrushStrokeSessions.values())) {
      if (!keep.has(session.layerId)) destroyRoundBrushStrokeSession(session);
    }
    for (const [id, target] of layerTargets.entries()) {
      if (!keep.has(id)) {
        deleteLayerTarget(target);
        layerTargets.delete(id);
      }
    }
    for (const layer of safeLayers) if (!layerTargets.has(layer.id)) createLayerTarget(layer.id);
    layerState = safeLayers;
    activeLayerId = keep.has(nextActiveLayerId) ? nextActiveLayerId : safeLayers[safeLayers.length - 1].id;
    syncActiveTarget();
    requestRender();
  }

  function setActiveLayer(id, layerName = '', ownerName = '') {
    const safeId = String(id || '').trim();
    if (!safeId) return false;
    if (!layerTargets.has(safeId)) {
      createLayerTarget(safeId);
      const safeOwnerName = String(ownerName || '').trim();
      const safeLayerName = String(layerName || '').trim() || safeOwnerName || 'User';
      layerState = [...layerState, { id: safeId, name: safeLayerName, ownerName: safeOwnerName || safeLayerName, visible: true, opacity: 1 }];
    }
    activeLayerId = safeId;
    syncActiveTarget();
    requestRender();
    return true;
  }

  function clearLayer(id = activeLayerId) {
    const target = layerTargets.get(id);
    if (!target) return;
    prepareDirectLayerWrite(id);
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.disable(gl.BLEND);
    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    if (oilPaintSystem) oilPaintSystem.clearLayer(id);
    finishDirectLayerWrite(id);
    target.containsPixels = false;
    if (id === activeLayerId) containsPixels = false;
    requestRender();
  }

  function getLayers() {
    return layerState.map((layer) => ({ ...layer, containsPixels: Boolean(layerTargets.get(layer.id)?.containsPixels) }));
  }

  function ensureLayer(id, layerName = '', ownerName = '', ownerClientId = '', ownerUserId = 0) {
    const safeId = String(id || '').trim();
    if (!safeId) return null;

    let target = layerTargets.get(safeId);
    if (!target) target = createLayerTarget(safeId);

    const existingIndex = layerState.findIndex((layer) => String(layer && layer.id ? layer.id : '') === safeId);
    const safeOwnerName = String(ownerName || '').trim();
    const safeOwnerClientId = String(ownerClientId || '').trim();
    const safeOwnerUserId = Number(ownerUserId) || 0;
    const safeLayerName = String(layerName || '').trim() || safeOwnerName || 'Layer 1';

    if (existingIndex < 0) {
      layerState = [...layerState, {
        id: safeId,
        name: safeLayerName,
        ownerName: safeOwnerName,
        ownerClientId: safeOwnerClientId,
        ownerUserId: safeOwnerUserId > 0 ? safeOwnerUserId : undefined,
        visible: true,
        opacity: 1,
      }];
    } else {
      layerState = layerState.map((layer, index) => index === existingIndex
        ? {
            ...layer,
            name: layer.name || safeLayerName,
            ownerName: layer.ownerName || safeOwnerName,
            ownerClientId: layer.ownerClientId || safeOwnerClientId,
            ownerUserId: layer.ownerUserId || (safeOwnerUserId > 0 ? safeOwnerUserId : undefined),
          }
        : layer);
    }

    return target;
  }

  function drawImageRegion(image, x, y, regionWidth, regionHeight, replaceRegion = true, layerId = activeLayerId) {
    const target = ensureLayer(layerId);
    if (!image || !target) return false;
    const texture = gl.createTexture();
    if (!texture) return false;
    prepareDirectLayerWrite(layerId);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, true);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, image);
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    if (replaceRegion) {
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
      gl.enable(gl.SCISSOR_TEST);
      gl.scissor(Math.floor(x), Math.max(0, documentHeight - Math.ceil(y + regionHeight)), Math.max(1, Math.ceil(regionWidth)), Math.max(1, Math.ceil(regionHeight)));
      gl.clearColor(0, 0, 0, 0);
      gl.clear(gl.COLOR_BUFFER_BIT);
      gl.disable(gl.SCISSOR_TEST);
    }
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(Math.floor(x), Math.max(0, documentHeight - Math.ceil(y + regionHeight)), Math.max(1, Math.ceil(regionWidth)), Math.max(1, Math.ceil(regionHeight)));
    gl.enable(gl.BLEND);
    gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
    gl.useProgram(displayProgram);
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    const position = attribLocation(displayProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, texture);
    gl.uniform1i(uniformLocation(displayProgram, 'u_texture'), 0);
    gl.uniform1f(uniformLocation(displayProgram, 'u_forceOpaque'), 0);
    gl.uniform1f(uniformLocation(displayProgram, 'u_opacity'), 1);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.disable(gl.BLEND);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.deleteTexture(texture);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    finishDirectLayerWrite(layerId);
    target.containsPixels = true;
    if (layerId === activeLayerId) containsPixels = true;
    requestRender();
    return true;
  }

  function readLayerRegion(x, y, regionWidth, regionHeight, layerId = activeLayerId) {
    const target = layerTargets.get(layerId);
    if (!target) return null;
    flushRoundBrushStrokeSessions(layerId);
    const safeX = Math.max(0, Math.floor(Number(x) || 0));
    const safeY = Math.max(0, Math.floor(Number(y) || 0));
    const safeWidth = Math.max(1, Math.min(documentWidth - safeX, Math.ceil(Number(regionWidth) || 1)));
    const safeHeight = Math.max(1, Math.min(documentHeight - safeY, Math.ceil(Number(regionHeight) || 1)));
    if (safeWidth <= 0 || safeHeight <= 0) return null;
    const pixels = new Uint8Array(safeWidth * safeHeight * 4);
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.readPixels(
      safeX,
      Math.max(0, documentHeight - safeY - safeHeight),
      safeWidth,
      safeHeight,
      gl.RGBA,
      gl.UNSIGNED_BYTE,
      pixels,
    );
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return { x: safeX, y: safeY, width: safeWidth, height: safeHeight, pixels };
  }

  function exportLayerDataUrl(layerId = activeLayerId) {
    const safeLayerId = String(layerId || activeLayerId || '').trim();
    if (!safeLayerId || typeof document === 'undefined') return '';
    const region = readLayerRegion(0, 0, documentWidth, documentHeight, safeLayerId);
    if (!region || !(region.pixels instanceof Uint8Array)) return '';

    const output = document.createElement('canvas');
    output.width = region.width;
    output.height = region.height;
    const context = output.getContext('2d');
    if (!context) return '';

    const imageData = context.createImageData(region.width, region.height);
    const rowBytes = region.width * 4;
    for (let sourceRow = 0; sourceRow < region.height; sourceRow += 1) {
      const targetRow = region.height - sourceRow - 1;
      const sourceOffset = sourceRow * rowBytes;
      const targetOffset = targetRow * rowBytes;
      imageData.data.set(
        region.pixels.subarray(sourceOffset, sourceOffset + rowBytes),
        targetOffset,
      );
    }
    context.putImageData(imageData, 0, 0);
    return output.toDataURL('image/png');
  }

  function drawPixelRegion(pixels, x, y, regionWidth, regionHeight, replaceRegion = true, layerId = activeLayerId) {
    const target = layerTargets.get(layerId);
    const safeWidth = Math.max(1, Math.floor(Number(regionWidth) || 1));
    const safeHeight = Math.max(1, Math.floor(Number(regionHeight) || 1));
    const safeX = Math.max(0, Math.floor(Number(x) || 0));
    const safeY = Math.max(0, Math.floor(Number(y) || 0));
    if (!target || !(pixels instanceof Uint8Array) || pixels.byteLength !== safeWidth * safeHeight * 4) return false;
    const targetY = Math.max(0, documentHeight - safeY - safeHeight);
    prepareDirectLayerWrite(layerId);
    // The caller supplies rows bottom-up already. Never inherit a flip left
    // behind by an earlier image upload.
    gl.pixelStorei(gl.UNPACK_FLIP_Y_WEBGL, false);
    gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, false);
    if (replaceRegion) {
      gl.bindTexture(gl.TEXTURE_2D, target.texture);
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
      gl.texSubImage2D(
        gl.TEXTURE_2D,
        0,
        safeX,
        targetY,
        safeWidth,
        safeHeight,
        gl.RGBA,
        gl.UNSIGNED_BYTE,
        pixels,
      );
      gl.bindTexture(gl.TEXTURE_2D, null);
    } else {
      const texture = gl.createTexture();
      if (!texture) return false;
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.NEAREST);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
      gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
      gl.pixelStorei(gl.UNPACK_ALIGNMENT, 1);
      gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, safeWidth, safeHeight, 0, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
      gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
      gl.viewport(safeX, targetY, safeWidth, safeHeight);
      gl.enable(gl.BLEND);
      gl.blendFuncSeparate(gl.ONE, gl.ONE_MINUS_SRC_ALPHA, gl.ONE, gl.ONE_MINUS_SRC_ALPHA);
      gl.useProgram(displayProgram);
      gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
      const position = attribLocation(displayProgram, 'a_position');
      gl.enableVertexAttribArray(position);
      gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
      gl.activeTexture(gl.TEXTURE0);
      gl.bindTexture(gl.TEXTURE_2D, texture);
      gl.uniform1i(uniformLocation(displayProgram, 'u_texture'), 0);
      gl.uniform1f(uniformLocation(displayProgram, 'u_forceOpaque'), 0);
      gl.uniform1f(uniformLocation(displayProgram, 'u_opacity'), 1);
      gl.drawArrays(gl.TRIANGLES, 0, 6);
      gl.disableVertexAttribArray(position);
      gl.disable(gl.BLEND);
      gl.bindFramebuffer(gl.FRAMEBUFFER, null);
      gl.bindTexture(gl.TEXTURE_2D, null);
      gl.deleteTexture(texture);
    }
    finishDirectLayerWrite(layerId);
    target.containsPixels = true;
    if (layerId === activeLayerId) containsPixels = true;
    requestRender();
    return true;
  }

  function ensureLiquifyTargets() {
    if (!liquifyScratchTexture) {
      liquifyScratchTexture = createTexture(gl, documentWidth, documentHeight);
      liquifyScratchFramebuffer = createFramebuffer(gl, liquifyScratchTexture);
    }
    if (!liquifyRestoreTexture) {
      liquifyRestoreTexture = createTexture(gl, documentWidth, documentHeight);
      liquifyRestoreFramebuffer = createFramebuffer(gl, liquifyRestoreTexture);
    }
  }

  function beginLiquifyStroke(layerId = activeLayerId) {
    const target = ensureLayer(layerId);
    if (!target) return false;
    ensureLiquifyTargets();
    flushRoundBrushStrokeSessions(layerId);
    drawTexture(target.texture, liquifyRestoreFramebuffer, documentWidth, documentHeight, false, 1, false);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    return true;
  }

  function getLiquifyModeIndex(mode) {
    const value = String(mode || 'flow_shift');
    const values = {
      smudge: 1,
      flow_shift: 1,
      clock_well: 1.75,
      counter_well: 2.25,
      collapse: 3,
      bloom: 4,
      shardfield: 5,
      veinflow: 6,
      faultline: 7,
      gravity_well: 8,
      waveglass: 9,
      water_reflection: 10,
      rim_shear: 11,
      restore: 12,
      soft_nudge: 13,
      field_paint: 14,
    };
    return values[value] || 1;
  }

  function drawLiquifyStroke(previousPoint, point, options = {}, layerId = activeLayerId) {
    const target = ensureLayer(layerId);
    if (!target || !previousPoint || !point) return false;
    ensureLiquifyTargets();
    const radius = Math.max(4, Math.min(440, Number(options.size) || 40));
    const deltaX = Number(point.x) - Number(previousPoint.x);
    const deltaY = Number(point.y) - Number(previousPoint.y);
    const distance = Math.hypot(deltaX, deltaY);
    const mode = String(options.liquifyMode || options.distortionMode || 'flow_shift');
    if (distance < 0.1 && mode !== 'restore') return false;
    const settings = options.liquifySettings && typeof options.liquifySettings === 'object' ? options.liquifySettings : {};
    const strengthRaw = options.intensity ?? options.strength ?? options.opacity ?? 0.55;
    const strength = Math.max(0.02, Math.min(1, Number(strengthRaw) > 1 ? Number(strengthRaw) / 100 : Number(strengthRaw) || 0.55));

    prepareDirectLayerWrite(layerId);
    drawTexture(target.texture, liquifyScratchFramebuffer, documentWidth, documentHeight, false, 1, false);
    gl.bindFramebuffer(gl.FRAMEBUFFER, target.framebuffer);
    gl.viewport(0, 0, documentWidth, documentHeight);
    gl.disable(gl.BLEND);
    gl.useProgram(liquifyProgram);
    applySelectionUniforms(liquifyProgram);
    applySurfaceContact(liquifyProgram, 'liquify', options);
    const smudgePressure = Number(point.inputPressure ?? point.pressure);
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_contactPressure'), Number.isFinite(smudgePressure) && smudgePressure > 0 ? clamp(smudgePressure, 0, 1) : 0.6);
    gl.bindBuffer(gl.ARRAY_BUFFER, quadBuffer);
    const position = attribLocation(liquifyProgram, 'a_position');
    gl.enableVertexAttribArray(position);
    gl.vertexAttribPointer(position, 2, gl.FLOAT, false, 0, 0);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, liquifyScratchTexture);
    gl.uniform1i(uniformLocation(liquifyProgram, 'u_source'), 0);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, liquifyRestoreTexture);
    gl.uniform1i(uniformLocation(liquifyProgram, 'u_restore'), 1);
    gl.uniform2f(uniformLocation(liquifyProgram, 'u_resolution'), documentWidth, documentHeight);
    gl.uniform2f(uniformLocation(liquifyProgram, 'u_center'), Number(point.x) || 0, Number(point.y) || 0);
    gl.uniform2f(uniformLocation(liquifyProgram, 'u_delta'), deltaX, deltaY);
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_radius'), radius);
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_strength'), strength);
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_force'), Math.max(0, Math.min(1, Number(settings.force ?? 0.5))));
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_flow'), Math.max(0, Math.min(1, Number(settings.flow ?? 0.5))));
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_twist'), Math.max(0, Math.min(1, Number(settings.twist ?? 0.5))));
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_chaos'), Math.max(0, Math.min(1, Number(settings.chaos ?? 0.5))));
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_detail'), Math.max(0, Math.min(1, Number(settings.detail ?? 0.5))));
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_edge'), Math.max(0, Math.min(1, Number(settings.edge ?? 0.5))));
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_seed'), Number(options.strokeSeed ?? options.seed ?? 0) || 0);
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_mode'), getLiquifyModeIndex(mode));
    gl.uniform1f(uniformLocation(liquifyProgram, 'u_hasRestore'), liquifyRestoreTexture ? 1 : 0);
    gl.drawArrays(gl.TRIANGLES, 0, 6);
    gl.disableVertexAttribArray(position);
    gl.activeTexture(gl.TEXTURE1);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.activeTexture(gl.TEXTURE0);
    gl.bindTexture(gl.TEXTURE_2D, null);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    finishDirectLayerWrite(layerId);
    target.containsPixels = true;
    if (layerId === activeLayerId) containsPixels = true;
    requestRender();
    return true;
  }

  function endLiquifyStroke() {
    return true;
  }

  function exportDataUrl(type = 'image/png', quality) {
    render(false, 0);
    const dataUrl = canvas.toDataURL(type, quality);
    render(true);
    return dataUrl;
  }

  function resize(nextWidth, nextHeight) {
    destroyLayerMoveSession();
    const safeWidth = Math.max(1, Math.floor(Number(nextWidth) || 1));
    const safeHeight = Math.max(1, Math.floor(Number(nextHeight) || 1));
    if (safeWidth === documentWidth && safeHeight === documentHeight) return;
    documentWidth = safeWidth;
    documentHeight = safeHeight;
    canvas.width = documentWidth;
    canvas.height = documentHeight;
    if (oilPaintSystem) oilPaintSystem.resize(documentWidth, documentHeight);
    createDocumentTarget();
    requestRender();
  }

  function destroy() {
    if (destroyed) return;
    destroyed = true;
    cancelScheduledRender();
    destroyLayerMoveSession();
    destroyAllRoundBrushStrokeSessions();
    if (oilPaintSystem) oilPaintSystem.destroy();
    oilPaintSystem = null;
    deleteDocumentTarget();
    gl.deleteBuffer(brushBuffer);
    gl.deleteBuffer(quadBuffer);
    gl.deleteBuffer(importedBuffer);
    gl.deleteBuffer(importedDabBuffer);
    gl.deleteBuffer(airbrushBuffer);
    for (const texture of importedTipTextures) gl.deleteTexture(texture);
    importedTipTextures.clear();
    if (selectionMaskTexture) gl.deleteTexture(selectionMaskTexture);
    gl.deleteProgram(brushProgram);
    gl.deleteProgram(paintProgram);
    gl.deleteProgram(roundCursorProgram);
    gl.deleteProgram(pencilCursorProgram);
    gl.deleteProgram(displayProgram);
    gl.deleteProgram(importedProgram);
    gl.deleteProgram(importedDabProgram);
    gl.deleteProgram(importedCompositeProgram);
    if (liquifyScratchFramebuffer) gl.deleteFramebuffer(liquifyScratchFramebuffer);
    if (liquifyScratchTexture) gl.deleteTexture(liquifyScratchTexture);
    if (liquifyRestoreFramebuffer) gl.deleteFramebuffer(liquifyRestoreFramebuffer);
    if (liquifyRestoreTexture) gl.deleteTexture(liquifyRestoreTexture);
    gl.deleteProgram(liquifyProgram);
    if (dryMedia) dryMedia.destroy();
    gl.deleteProgram(airbrushProgram);
  }

  canvas.width = documentWidth;
  canvas.height = documentHeight;
  createDocumentTarget();
  render();

  return {
    clear,
    setTransparentPaper,
    beginLayerMove,
    beginLiquifyStroke,
    beginPaintStroke,
    beginRoundBrushStroke,
    beginImportedStroke,
    drawImportedBrushDabsV2,
    redrawImportedStroke,
    captureSnapshot,
    destroy,
    destroySnapshot,
    drawAirbrushDabs,
    drawImageRegion,
    drawPixelRegion,
    drawImportedBrushDabs,
    drawPaintImportedBrushDabs,
    drawPaintStroke,
    dryPaintLayer,
    drawEllipseBrushStroke,
    drawJitterSegment,
    drawLiquifyStroke,
    drawPencilDabs,
    drawRoundBrushStroke,
    drawSegment,
    endLayerMove,
    endLiquifyStroke,
    endPaintStroke,
    whenPaintIdle,
    endRoundBrushStroke,
    exportDataUrl,
    exportLayerDataUrl,
    exportPaintLayerState,
    getActiveLayerId: () => activeLayerId,
    getCanvas: () => canvas,
    getLayers,
    getPaintDebugMetrics,
    getPaperSurface,
    setPaperSurface,
    ensureLayer,
    hasPixels: () => Array.from(layerTargets.values()).some((target) => target.containsPixels) || Boolean(oilPaintSystem && oilPaintSystem.hasPaint()),
    hideImportedBrushCursor,
    hidePencilCursor,
    hideRoundBrushCursor,
    importPaintLayerState,
    loadImage,
    render,
    renderBaseLayer,
    renderLayer,
    readLayerRegion,
    setActiveLayer,
    setLayers,
    setPaintDisplaySettings,
    setSelectionMask,
    resize,
    restoreSnapshot,
    restoreSnapshotLayers,
    clearLayer,
    clearPaintLayer,
    updateImportedBrushCursor,
    updateLayerMove,
    updatePencilCursor,
    updateRoundBrushCursor,
  };
}
